import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
    Container,
    Typography,
    Box,
    Tabs,
    Tab,
    TextField,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Paper,
    Chip,
    Grid,
    Link,
    useMediaQuery,
    useTheme,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Stepper,
    Step,
    StepLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import MessageIcon from '@mui/icons-material/Message';
import BusinessIcon from '@mui/icons-material/Business';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SchoolIcon from '@mui/icons-material/School';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupIcon from '@mui/icons-material/Group';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EmailIcon from '@mui/icons-material/Email';
import CategoryIcon from '@mui/icons-material/Category';
import BarChartIcon from '@mui/icons-material/BarChart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import CloseIcon from '@mui/icons-material/Close';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SEO from '../components/SEO';

const iOSImages = [
    '/pic/IOSimage0.png',
    '/pic/IOSimage1.png',
    '/pic/IOSimage2.png',
    '/pic/IOSimage3.png',
];

const androidImages = [
    '/pic/android-1.jpg',
    '/pic/android-2.jpg',
    '/pic/android-3.jpg',
    '/pic/android-4.jpg',
];

const IOSInstallModal = ({ open, handleClose }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeStep, setActiveStep] = useState(0);

    const steps = [
        {
            label: 'Step 1: Open menu (three dots)',
            description: 'Open irontrack.ee on your iPhone with Safari browser, then tap the three-dot menu (...) in the bottom-right corner.',
            image: iOSImages[0],
        },
        {
            label: 'Step 2: Tap Share',
            description: "In the menu that opens, tap 'Share'.",
            image: iOSImages[1],
        },
        {
            label: 'Step 3: Choose Add to Home Screen',
            description: "In the share sheet, scroll down and tap 'Add to Home Screen'.",
            image: iOSImages[2],
        },
        {
            label: 'Step 4: Confirm with Add',
            description: "Review the app name if needed, keep 'Open as Web App' enabled, then tap 'Add' in the top-right corner.",
            image: iOSImages[3],
        },
    ];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
            fullScreen={isMobile}
        >
            <DialogTitle
                sx={{
                    bgcolor: '#0072E5',
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 3,
                    py: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AppleIcon />
                    <Typography variant="h6">Install IronTrack on iOS</Typography>
                </Box>
                <IconButton edge="end" color="inherit" onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3, mt: 2 }}>
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                    {steps.map((step) => (
                        <Step key={step.label}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        {steps[activeStep].label}
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                        {steps[activeStep].description}
                    </Typography>
                    <Box
                        component="img"
                        src={steps[activeStep].image}
                        alt={`iOS installation step ${activeStep + 1}`}
                        sx={{
                            maxWidth: '100%',
                            height: 'auto',
                            maxHeight: '550px',
                            border: '1px solid #eee',
                            borderRadius: 2,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            mb: 2,
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                <Button
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep(activeStep - 1)}
                    variant="outlined"
                >
                    Back
                </Button>
                <Box>
                    {activeStep === steps.length - 1 ? (
                        <Button onClick={handleClose} variant="contained" color="primary">
                            Done
                        </Button>
                    ) : (
                        <Button onClick={() => setActiveStep(activeStep + 1)} variant="contained" color="primary">
                            Next
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

const AndroidInstallModal = ({ open, handleClose }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeStep, setActiveStep] = useState(0);

    const steps = [
        {
            label: 'Read all 4 steps first! - Open Chrome',
            description: 'This feature works best in Chrome browser on Android. Open your website in Chrome.',
            image: androidImages[0],
        },
        {
            label: 'Tap Menu Button',
            description: 'Tap the menu button (three dots) in the top right corner of your browser.',
            image: androidImages[1],
        },
        {
            label: 'Add to Home Screen',
            description: "Scroll down and tap 'Add to Home screen' from the menu options.",
            image: androidImages[2],
        },
        {
            label: 'Confirm Installation',
            description: "Tap 'Add' in the confirmation dialog to install the app on your home screen.",
            image: androidImages[3],
        },
    ];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
            fullScreen={isMobile}
        >
            <DialogTitle
                sx={{
                    bgcolor: '#3ddc84',
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 3,
                    py: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AndroidIcon />
                    <Typography variant="h6">Install IronTrack on Android</Typography>
                </Box>
                <IconButton edge="end" color="inherit" onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3, mt: 2 }}>
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                    {steps.map((step) => (
                        <Step key={step.label}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        {steps[activeStep].label}
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                        {steps[activeStep].description}
                    </Typography>
                    <Box
                        component="img"
                        src={steps[activeStep].image}
                        alt={`Android installation step ${activeStep + 1}`}
                        sx={{
                            maxWidth: '100%',
                            height: 'auto',
                            maxHeight: '550px',
                            border: '1px solid #eee',
                            borderRadius: 2,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            mb: 2,
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                <Button
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep(activeStep - 1)}
                    variant="outlined"
                >
                    Back
                </Button>
                <Box>
                    {activeStep === steps.length - 1 ? (
                        <Button
                            onClick={handleClose}
                            variant="contained"
                            sx={{ bgcolor: '#3ddc84', '&:hover': { bgcolor: '#2bb56a' } }}
                        >
                            Done
                        </Button>
                    ) : (
                        <Button
                            onClick={() => setActiveStep(activeStep + 1)}
                            variant="contained"
                            sx={{ bgcolor: '#3ddc84', '&:hover': { bgcolor: '#2bb56a' } }}
                        >
                            Next
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

const Help = () => {
    const { isLoggedIn, role } = useContext(AuthContext);
    const [tabValue, setTabValue] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredHelp, setFilteredHelp] = useState([]);
    const [iOSModalOpen, setIOSModalOpen] = useState(false);
    const [androidModalOpen, setAndroidModalOpen] = useState(false);
    const [androidInstallPromptEvent, setAndroidInstallPromptEvent] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isAffiliateOwner = isLoggedIn && role === 'affiliate';

    useEffect(() => {
        const handleBeforeInstallPrompt = (event) => {
            event.preventDefault();
            setAndroidInstallPromptEvent(event);
        };

        const handleAppInstalled = () => {
            setAndroidInstallPromptEvent(null);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    useEffect(() => {
        if (!isAffiliateOwner && tabValue === 4) {
            setTabValue(0);
        }
    }, [isAffiliateOwner, tabValue]);

    const isAndroidChromeOrEdge = () => {
        const userAgent = navigator.userAgent || '';
        const isAndroid = /Android/i.test(userAgent);
        const isEdge = /EdgA?\//i.test(userAgent);
        const isChrome =
            /Chrome\//i.test(userAgent) &&
            !/EdgA?\//i.test(userAgent) &&
            !/OPR\//i.test(userAgent) &&
            !/SamsungBrowser\//i.test(userAgent);

        return isAndroid && (isEdge || isChrome);
    };

    const handleAndroidInstall = async () => {
        // Only Chrome/Edge on Android gets native prompt. Others see manual steps.
        if (!isAndroidChromeOrEdge()) {
            setAndroidModalOpen(true);
            return;
        }

        if (!androidInstallPromptEvent) {
            setAndroidModalOpen(true);
            return;
        }

        androidInstallPromptEvent.prompt();
        await androidInstallPromptEvent.userChoice;
        setAndroidInstallPromptEvent(null);
    };

    // Define all help content
    const helpContent = {
        gettingStarted: [
            {
                title: 'Creating an Account',
                content: (
                    <>
                        <Typography paragraph>
                            To get started with Irontrack, you need to create an account:
                        </Typography>
                        <ol>
                            <li>Click on the "Join Us" button on the homepage</li>
                            <li>Fill in your personal details</li>
                            <li>Validate Email</li>
                            <li>Check the Terms and Conditions checkbox</li>
                            <li>Click "Join Us" to create your account</li>
                            <li>Check your email for a confirmation link</li>
                        </ol>
                        <Typography variant="subtitle2" sx={{ mt: 2 }}>
                            Note: If you're an affiliate owner contact with e-mail info@irontrack.ee.
                        </Typography>
                    </>
                ),
                tags: ['account', 'registration', 'signup', 'join'],
                roles: ['all']
            },
            {
                title: 'Logging In',
                content: (
                    <>
                        <Typography paragraph>
                            To log in to your Irontrack account:
                        </Typography>
                        <ol>
                            <li>Click the "Log In" button on the top-right corner of the homepage</li>
                            <li>Enter your email address and password</li>
                            <li>Click "Log In" to access your account</li>
                        </ol>
                        <Typography variant="subtitle2" sx={{ mt: 2 }}>
                            If you've forgotten your password, click the "Forgot Password" link on the login page. You'll receive an email with instructions to reset your password.
                        </Typography>
                    </>
                ),
                tags: ['login', 'password', 'access', 'forgot password', 'reset password'],
                roles: ['all']
            },
            {
                title: 'Selecting Your Role',
                content: (
                    <>
                        <Typography paragraph>
                            After logging in, you'll be prompted to select your role:
                        </Typography>
                        <ul>
                            <li><strong>Regular User:</strong> For gym members who want to register for classes and track their progress</li>
                            <li><strong>Trainer:</strong> For coaches who manage classes and members</li>
                            <li><strong>Affiliate Owner:</strong> For gym owners who manage all aspects of their facility</li>
                            <li><strong>Check-in:</strong> For front desk check-in operations at your gym</li>
                        </ul>
                        <Typography paragraph>
                            Your available roles depend on your account settings. Contact your gym administrator if you need a role that isn't available to you.
                        </Typography>
                    </>
                ),
                tags: ['role', 'profile', 'user type', 'permissions'],
                roles: ['all']
            },
            {
                title: 'Dashboard Navigation',
                content: (
                    <>
                        <Typography paragraph>
                            The Irontrack dashboard provides easy access to all features:
                        </Typography>
                        <ul>
                            <li><strong>Training Log:</strong> Create, Edit and View your trainings</li>
                            <li><strong>Profile:</strong> View and edit your personal information</li>
                            <li><strong>Trainings:</strong> Choose gym, buy plan and register for training</li>
                            <li><strong>Records:</strong> Track your personal records and achievements</li>
                        </ul>
                        <Typography paragraph>
                            The dashboard adapts to your selected role, showing relevant options and features for regular users, trainers, or affiliate owners.
                        </Typography>
                        <Typography paragraph>
                            On mobile devices, you'll find navigation options at the bottom of the screen for quick access to key features.
                        </Typography>
                    </>
                ),
                tags: ['dashboard', 'navigation', 'menu', 'features', 'layout', 'mobile navigation'],
                roles: ['all']
            },
            {
                title: 'Installing Irontrack App (iOS & Android)',
                content: (
                    <>
                        <Typography paragraph>
                            You can install Irontrack to your home screen for a faster app-like experience.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1 }}>
                            <Button
                                variant="contained"
                                startIcon={<AppleIcon />}
                                onClick={() => setIOSModalOpen(true)}
                                sx={{
                                    py: 1.2,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    backgroundColor: '#000000',
                                    color: '#ffffff',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#222222',
                                    },
                                }}
                            >
                                Install on iOS
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<AndroidIcon />}
                                onClick={handleAndroidInstall}
                                sx={{
                                    py: 1.2,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    backgroundColor: '#3ddc84 !important',
                                    color: '#ffffff !important',
                                    textTransform: 'none',
                                    '& .MuiButton-startIcon': {
                                        color: '#ffffff',
                                    },
                                    '&:hover': {
                                        backgroundColor: '#2bb56a !important',
                                    },
                                }}
                            >
                                Install on Android
                            </Button>
                        </Box>
                    </>
                ),
                tags: ['install', 'pwa', 'ios', 'android', 'home screen', 'app'],
                roles: ['all']
            },

        ],
        regularUsers: [
            {
                title: 'Managing Your Profile',
                content: (
                    <>
                        <Typography paragraph>
                            To update your profile information:
                        </Typography>
                        <ol>
                            <li>Click on "My Profile" in the main menu</li>
                            <li>You'll see your profile information displayed</li>
                            <li>Click "Edit Profile" to update your personal information</li>
                            <li>Upload a profile picture by clicking "Upload Profile Picture"</li>
                            <li>Update your emergency contact information</li>
                            <li>Save your changes</li>
                        </ol>
                        <Typography paragraph>
                            You can also change your password, view your statistics, purchase history, visit history, active plans, credit, and contracts through the tabs in your profile page.
                        </Typography>
                        <Typography paragraph>
                            Keep your information up-to-date, especially your emergency contact information for safety purposes.
                        </Typography>
                    </>
                ),
                tags: ['profile', 'personal information', 'contact details', 'account settings', 'upload photo', 'edit profile'],
                roles: ['regular']
            },
            {
                title: 'Registering for Classes',
                content: (
                    <>
                        <Typography paragraph>
                            To register for a training class:
                        </Typography>
                        <ol>
                            <li>Navigate to the "Trainings" or "Register for training" page from the main menu</li>
                            <li>Find gym, click "View Classes".</li>
                            <li>Browse available classes by date using the week navigation or toggle to weekly view</li>
                            <li>Use the Day selector at the top to choose which day's classes to display</li>
                            <li>Click on a class to view details</li>
                            <li>In the class details modal, click the "Register" button</li>
                            <li>If needed, select a payment plan</li>
                        </ol>
                        <Typography paragraph>
                            Different class types have distinct background colors in the schedule, making it easy to identify the type of workout.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'warning.main', mt: 1 }}>
                            Note: Some classes may require a valid plan or payment. Free classes are marked accordingly.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Important: You can only register for classes that match the "Training Types" covered by your plan. For example, if your plan only includes "WOD" classes, you won't be able to register for "Weightlifting" classes.
                        </Typography>
                    </>
                ),
                tags: ['class', 'registration', 'training', 'register', 'sign up', 'classes', 'schedule'],
                roles: ['regular']
            },
            {
                title: 'Canceling Class Registration',
                content: (
                    <>
                        <Typography paragraph>
                            If you need to cancel your registration for a class:
                        </Typography>
                        <ol>
                            <li>Navigate to the "Classes" page</li>
                            <li>Find and click on the class you're registered for</li>
                            <li>In the class details modal, click the "Cancel Registration" button</li>
                            <li>Confirm your cancellation when prompted</li>
                        </ol>
                        <Typography variant="subtitle2" sx={{ color: 'error.main', mt: 1 }}>
                            Note: Please cancel your registration if you cannot attend a class. This allows other members to take your spot, especially for classes with limited capacity.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                            Some gyms may have cancellation policies with time restrictions. Check with your affiliate for specific rules.
                        </Typography>
                    </>
                ),
                tags: ['cancel', 'registration', 'class', 'withdraw', 'unregister'],
                roles: ['regular']
            },
            {
                title: 'Using the Waitlist Feature',
                content: (
                    <>
                        <Typography paragraph>
                            If a class is full, you can join the waitlist:
                        </Typography>
                        <ol>
                            <li>Navigate to the "Classes" page</li>
                            <li>Click on the class that shows as full</li>
                            <li>In the class details modal, click the "Join Waitlist" button</li>
                            <li>If required, select an appropriate plan</li>
                            <li>Confirm joining the waitlist</li>
                        </ol>
                        <Typography paragraph>
                            If someone cancels their registration, people on the waitlist will automatically be registered in the order they joined the waitlist.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            You'll receive a notification if you're moved from the waitlist to the registered attendees.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                            You can remove yourself from the waitlist at any time by clicking the "Remove from Waitlist" button in the class details.
                        </Typography>
                    </>
                ),
                tags: ['waitlist', 'full class', 'waiting', 'capacity', 'registration'],
                roles: ['regular']
            },
            {
                title: 'Push Notifications & Consent (Member)',
                content: (
                    <>
                        <Typography paragraph>
                            Irontrack can send push notifications to your phone/PWA app for important updates from your affiliate.
                        </Typography>
                        <Typography paragraph>
                            When you log in and open the app, you may see a consent dialog:
                        </Typography>
                        <ul>
                            <li><strong>Yes:</strong> browser/OS permission is requested and push is activated</li>
                            <li><strong>No:</strong> your choice is saved and the dialog is not shown again automatically</li>
                        </ul>
                        <Typography paragraph>
                            Examples of notifications:
                        </Typography>
                        <ul>
                            <li>Affiliate announcements (currently active notification type)</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Important: push permission is managed by your device/browser settings.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                            If you previously chose No, enable notifications again from your device/browser settings for Irontrack.
                        </Typography>
                        <Typography paragraph sx={{ mt: 2 }}>
                            How to turn notifications off or on later:
                        </Typography>
                        <ul>
                            <li><strong>iPhone (PWA):</strong> Settings {'>'} Notifications {'>'} Irontrack {'>'} Allow Notifications (off/on)</li>
                            <li><strong>Android (Chrome/PWA):</strong> Chrome (or phone) site/app notification settings for Irontrack {'>'} set to Block or Allow</li>
                            <li><strong>If prompt does not appear again:</strong> reset site notification permission to "Ask" in browser settings</li>
                        </ul>
                    </>
                ),
                tags: ['push notifications', 'consent', 'allow', 'decline', 'member notifications', 'pwa notifications', 'turn off notifications', 'turn on notifications'],
                roles: ['regular']
            },
            {
                title: 'Tracking Your Records',
                content: (
                    <>
                        <Typography paragraph>
                            Irontrack allows you to track your workout performance and records:
                        </Typography>
                        <ol>
                            <li>Navigate to the "Records" section in the main menu</li>
                            <li>Choose the record type: WOD, Weightlifting, or Cardio</li>
                            <li>View your historical performance data by record type</li>
                            <li>Click on any record to see detailed information</li>
                            <li>Add new records by clicking "Add Record"</li>
                            <li>For existing exercise names, select from the dropdown or enter a new one</li>
                            <li>Enter the date and performance details specific to your record type</li>
                            <li>Click "Save Record" to add it to your records</li>
                        </ol>
                        <Typography paragraph>
                            In the record details, you can view charts showing your progress over time. You can toggle between line and bar charts to visualize your data differently.
                        </Typography>
                        <Typography paragraph>
                            Progress indicators show how you're improving over time. Green arrows indicate improvement, while red arrows show a decrease in performance.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Tip: Use the Records feature to track your PRs (Personal Records) and monitor your progress over time. This helps you set realistic goals and celebrate achievements.
                        </Typography>
                    </>
                ),
                tags: ['records', 'performance', 'tracking', 'statistics', 'workout data', 'pr', 'personal records', 'charts', 'progress'],
                roles: ['regular']
            },
            {
                title: 'Your Training Diary',
                content: (
                    <>
                        <Typography paragraph>
                            To track your workouts in the Training Diary:
                        </Typography>
                        <ol>
                            <li>Navigate to the "Trainings" page from the main menu</li>
                            <li>Click "Add New Training" to log a workout</li>
                            <li>Select the training type (WOD, Weightlifting, Cardio, Rowing, Gymnastics, or Other)</li>
                            <li>Enter the date of your training</li>
                            <li>For WODs, you can search existing WODs or create a custom one</li>
                            <li>Enter exercises, reps, sets, weights, or scores as needed</li>
                            <li>Click "Save Training" to add it to your diary</li>
                        </ol>
                        <Typography paragraph>
                            Your trainings will appear in the calendar below. Click on any training in the calendar to view or edit details.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            The Training Diary helps you maintain a complete history of your workouts, even for sessions you complete outside of scheduled classes.
                        </Typography>
                    </>
                ),
                tags: ['training diary', 'workouts', 'log', 'calendar', 'training history', 'exercise log'],
                roles: ['regular']
            },
            {
                title: 'Viewing Your Active Plans',
                content: (
                    <>
                        <Typography paragraph>
                            To view your active membership plans:
                        </Typography>
                        <ol>
                            <li>Go to "My Profile" in the main menu</li>
                            <li>Select the "Active Memberships" tab</li>
                            <li>View all your active plans, including:</li>
                            <ul>
                                <li>Plan name and training type</li>
                                <li>Start and end dates</li>
                                <li>Price</li>
                                <li>Sessions left (or "Unlimited" for unlimited plans)</li>
                            </ul>
                            <li>Click on a plan row to expand and view more details</li>
                        </ol>
                        <Typography paragraph>
                            The system shows days left until expiration and highlights sessions left with color codes: green (many left), yellow (few left), or red (none left).
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Tip: Keep track of your plan expirations to ensure you renew before they expire and avoid interruptions in your training schedule.
                        </Typography>
                    </>
                ),
                tags: ['active plans', 'membership', 'sessions', 'expiration', 'subscriptions'],
                roles: ['regular']
            },
            {
                title: 'Viewing Class Leaderboards',
                content: (
                    <>
                        <Typography paragraph>
                            After attending a class, you can view the leaderboard and add your score:
                        </Typography>
                        <ol>
                            <li>Go to the "Classes" page</li>
                            <li>Click on a class you've attended</li>
                            <li>In the class details, click the "Leaderboard" button</li>
                            <li>View scores from other participants</li>
                            <li>Click "Add Score" to enter your performance</li>
                            <li>Choose your score type (RX, Scaled, or Beginner)</li>
                            <li>Enter your score and save</li>
                        </ol>
                        <Typography paragraph>
                            You can filter the leaderboard to view all scores or only specific score types (RX, Scaled, or Beginner).
                        </Typography>
                        <Typography variant="subtitle2" sx={{ mt: 2 }}>
                            Leaderboards help you track your performance against others and monitor your progress.
                        </Typography>
                    </>
                ),
                tags: ['leaderboard', 'scores', 'competition', 'results', 'performance'],
                roles: ['regular']
            },
            {
                title: 'Contracts',
                content: (
                    <>
                        <Typography paragraph>
                            A contract is a recurring monthly membership. One-time plans (class packs, monthly packs) are separate — see "Purchasing a Plan". Gyms can offer contracts in two ways on the Buy Plans page, or they can send you a contract directly.
                        </Typography>
                        <ul>
                            <li><strong>Join contract:</strong> You start it yourself, accept the terms, and pay the first invoice. The contract is created immediately after payment.</li>
                            <li><strong>Request contract:</strong> You send a request by email. The gym still creates the contract afterwards, then you accept it in your profile.</li>
                            <li><strong>Sent by the gym:</strong> The gym creates a contract for you. You accept it under My Profile → Contracts.</li>
                        </ul>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                            Viewing your contracts:
                        </Typography>
                        <ol>
                            <li>Navigate to "My Profile"</li>
                            <li>Select the "Contracts" tab</li>
                            <li>View all your active and past contracts</li>
                            <li>Click on a contract to see details including:</li>
                            <ul>
                                <li>Contract type and status</li>
                                <li>Payment information</li>
                                <li>Valid dates</li>
                                <li>Payment holidays</li>
                            </ul>
                            <li>For contracts waiting acceptance, review terms and click "Accept Contract"</li>
                        </ol>
                        <Typography paragraph>
                            Joined and gym-created contracts stay visible here after you accept them, together with the date you accepted.
                        </Typography>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                            Joining a membership contract:
                        </Typography>
                        <Typography paragraph>
                            Some gyms let you start a monthly contract yourself. It appears on Buy Plans under "Membership contracts".
                        </Typography>
                        <ol>
                            <li>Go to "Register Training" in the main menu</li>
                            <li>Select your gym if it is not already selected</li>
                            <li>Click "Buy Plans"</li>
                            <li>Scroll to "Membership contracts"</li>
                            <li>Click "Join contract" on the offering you want</li>
                        </ol>
                        <Typography paragraph>
                            You only enter two things. Everything else (price, length, training types, contract text) is set by the gym:
                        </Typography>
                        <ul>
                            <li><strong>Start date:</strong> The day your membership begins. It must be today or later, and between the 1st and 28th of a month.</li>
                            <li><strong>Monthly payment day:</strong> The calendar day (1–28) when each monthly fee is due. Some gyms let you pick any day; others offer only specific days.</li>
                        </ul>
                        <Typography paragraph>
                            The first payment updates automatically when you change either date:
                        </Typography>
                        <ul>
                            <li>If the start date and payment day are the same calendar day, the first payment is one full month.</li>
                            <li>If they differ, the first payment is prorated for the days until the next payment day, and may also include the following full month.</li>
                            <li>If the gym offers an introductory discount, it is applied to this first payment.</li>
                        </ul>
                        <Typography paragraph>
                            The preview also shows the next payment date, when the contract ends, and the earliest date you can request termination.
                        </Typography>
                        <ol>
                            <li>Read the contract text in the dialog</li>
                            <li>Tick that you agree to the contract text</li>
                            <li>Tick that you have read the Terms and Conditions (open them with "Open Terms")</li>
                            <li>Tick the withdrawal acknowledgement: the service starts on the contract start date, which is the first day of the 14-day withdrawal period</li>
                            <li>Click "Review and pay"</li>
                            <li>Confirm the order. This places an order with an obligation to pay</li>
                            <li>Complete payment on the checkout page (card, bank, or available credit)</li>
                        </ol>
                        <Typography paragraph>
                            The contract becomes active only after the first payment succeeds. Until then it stays waiting for acceptance.
                        </Typography>
                        <Typography paragraph>
                            Because the contract is concluded at a distance, you can withdraw without giving a reason. The 14-day period starts on the contract start date. The join dialog shows the exact deadline for the date you picked. To withdraw, send a notice to the gym before that date. Access begins on the start date, so you must expressly request that the service starts then. If you withdraw after the service has begun, you pay for the part already provided. You lose the right of withdrawal once the service has been fully performed.
                        </Typography>
                        <ul>
                            <li>A payment link for the next monthly fee is emailed to you automatically 10 days before each due date.</li>
                            <li>The gym may require the contract to stay active for a minimum number of months before you can request termination. That period and the earliest possible end date are shown in the join dialog.</li>
                            <li>If you want to pay with Stebby, contact the gym. Self-service join is for card, bank, or Irontrack credit.</li>
                        </ul>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                            Requesting a contract:
                        </Typography>
                        <Typography paragraph>
                            Some gyms ask you to request a contract instead of joining it yourself. That offering appears on Buy Plans under "Contracts". This does not create a contract and does not take payment.
                        </Typography>
                        <ol>
                            <li>Go to "Register Training" → "Buy Plans"</li>
                            <li>Scroll to "Contracts"</li>
                            <li>Click "Request contract"</li>
                            <li>Choose a start date (1st–28th) and a monthly payment day</li>
                            <li>Answer any extra questions the gym has added</li>
                            <li>Click "Review request", then "Confirm and submit"</li>
                        </ol>
                        <Typography paragraph>
                            The request is emailed to the gym and a confirmation copy is emailed to you. The answers are not stored in Irontrack. After the gym creates the contract, you accept it under My Profile → Contracts, then pay as instructed.
                        </Typography>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                            Payment holidays:
                        </Typography>
                        <Typography paragraph>
                            You can request payment holidays for monthly contracts if you'll be away. These requests need approval from your gym. Payment holidays temporarily suspend payments, but also restrict your access to classes during that period.
                        </Typography>
                    </>
                ),
                tags: ['contracts', 'agreements', 'terms', 'payment holidays', 'membership contract', 'base contract', 'contract request', 'join', 'monthly', 'withdrawal', 'prorated', 'payment day', 'recurring'],
                roles: ['regular']
            },
            {
                title: 'Purchasing a Plan',
                content: (
                    <>
                        <Typography paragraph>
                            To purchase a training plan:
                        </Typography>
                        <ol>
                            <li>Go to "Register Training" in the main menu</li>
                            <li>Search for your affiliate if not already selected</li>
                            <li>Click "Buy Plans" to see available plans</li>
                            <li>Choose a plan that suits your needs</li>
                            <li>Click "Buy Plan"</li>
                            <li>You'll be redirected to the checkout page</li>
                            <li>Review your purchase and payment details</li>
                            <li>Apply any available credit if desired</li>
                            <li>Complete your purchase</li>
                        </ol>
                        <Typography paragraph>
                            After purchasing, your plan will be immediately active and visible in your Active Plans section.
                        </Typography>
                        <Typography paragraph>
                            Recurring memberships on the same page (Join contract / Request contract) are documented under "Contracts".
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Pay attention to the "Training Types" included in each plan to ensure it covers the classes you want to attend. Some plans may be restricted to specific types of classes.
                        </Typography>
                    </>
                ),
                tags: ['purchase', 'plans', 'buy', 'checkout', 'payment'],
                roles: ['regular']
            },
            {
                title: 'Managing Your Credit',
                content: (
                    <>
                        <Typography paragraph>
                            To view and use your credit:
                        </Typography>
                        <ol>
                            <li>Go to "My Profile" in the main menu</li>
                            <li>Click on the "Credit" tab</li>
                            <li>View your current credit balance</li>
                            <li>See your credit history, including amounts and dates</li>
                            <li>When making purchases, you can apply available credit at checkout</li>
                        </ol>
                        <Typography paragraph>
                            Credit can be used for purchasing plans or paying for classes. Your affiliate owner can add credit to your account if needed.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            You can use partial credit for purchases. For example, if you have €20 credit and want to buy a €50 plan, you can apply the €20 credit and pay the remaining €30.
                        </Typography>
                    </>
                ),
                tags: ['credit', 'balance', 'funds', 'payment'],
                roles: ['regular']
            },
            {
                title: 'Setting Your Home Gym',
                content: (
                    <>
                        <Typography paragraph>
                            Setting a home gym makes accessing classes easier:
                        </Typography>
                        <ol>
                            <li>Go to "Register Training" in the main menu</li>
                            <li>Search for your preferred affiliate</li>
                            <li>Click "Add as Home Gym"</li>
                            <li>Now when you log in, you'll be automatically directed to your home gym's classes</li>
                            <li>You can change or remove your home gym at any time</li>
                        </ol>
                        <Typography paragraph>
                            Having a home gym set makes it faster to register for classes at your regular location.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Even with a home gym set, you can still view and register for classes at other affiliates if needed.
                        </Typography>
                    </>
                ),
                tags: ['home gym', 'default affiliate', 'preferred gym'],
                roles: ['regular']
            },
            {
                title: 'Understanding Training Types',
                content: (
                    <>
                        <Typography paragraph>
                            Irontrack supports various training types to categorize workouts:
                        </Typography>
                        <ul>
                            <li><strong>WOD (Workout of the Day):</strong> Daily programmed workouts, often with varied movements</li>
                            <li><strong>Weightlifting:</strong> Focused on Olympic weightlifting movements and strength training</li>
                            <li><strong>Cardio:</strong> Endurance-focused workouts</li>
                            <li><strong>Rowing:</strong> Specialized rowing sessions</li>
                            <li><strong>Gymnastics:</strong> Body weight and skill-focused movements</li>
                            <li><strong>Open Gym:</strong> Flexible training sessions where you work on your own</li>
                            <li><strong>Kids:</strong> Classes designed for children</li>
                            <li><strong>Basic:</strong> Basic training sessions for beginners</li>
                            <li><strong>Other:</strong> Any other training type not covered by the above categories</li>
                        </ul>
                        <Typography paragraph>
                            When purchasing plans, check which training types are included. Your plan must cover a specific training type for you to register for classes of that type.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Some plans include "All Classes" which gives you access to every training type offered by your gym.
                        </Typography>
                    </>
                ),
                tags: ['training types', 'workout types', 'class categories', 'wod', 'weightlifting', 'cardio'],
                roles: ['regular']
            },
            {
                title: 'Using Gift Cards',
                content: (
                    <>
                        <Typography paragraph>
                            Redeem gift cards to add credit to your account:
                        </Typography>
                        <ol>
                            <li>Navigate to the "Credit" section from your profile menu</li>
                            <li>Scroll to the "Gift Card" section</li>
                            <li>Select the gym/affiliate where the gift card is valid</li>
                            <li>Enter the gift card code</li>
                            <li>Click "Verify" to check the gift card details</li>
                            <li>Review the gift card information (amount, expiry date)</li>
                            <li>Click "Redeem" to add the credit to your account</li>
                        </ol>
                        <Typography paragraph>
                            After redemption:
                        </Typography>
                        <ul>
                            <li>The gift card amount is added to your credit balance for the selected gym</li>
                            <li>You can use this credit when purchasing plans or paying for classes</li>
                            <li>The credit is specific to the gym where the gift card was issued</li>
                            <li>View your redeemed gift cards in the "Redeemed Gift Cards" section</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Gift cards must be redeemed before their expiry date. Once redeemed, the credit doesn't expire and can be used whenever you want.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'warning.main', mt: 1 }}>
                            Make sure to select the correct gym when redeeming - gift cards are only valid for the gym that issued them.
                        </Typography>
                    </>
                ),
                tags: ['gift cards', 'redeem', 'vouchers', 'credits', 'balance'],
                roles: ['regular']
            },
            {
                title: 'Registering for Classes',
                content: (
                    <>
                        <Typography paragraph>
                            To register for a class:
                        </Typography>
                        <ol>
                            <li>Go to the "Classes" section</li>
                            <li>Select your gym from the list or use the search</li>
                            <li>Navigate to the desired day using the calendar or day selector</li>
                            <li>View available classes for that day</li>
                            <li>Click on a class to see full details:</li>
                            <ul>
                                <li>Training type and name</li>
                                <li>Trainer</li>
                                <li>Time and duration</li>
                                <li>Available spots</li>
                                <li>WOD (Workout of the Day) if applicable</li>
                            </ul>
                            <li>Click "Register" to sign up</li>
                            <li>If the class is full, you can join the waitlist</li>
                        </ol>
                        <Typography paragraph>
                            Important notes:
                        </Typography>
                        <ul>
                            <li>You need an active plan that covers the class training type</li>
                            <li>Registering for a class uses one session from your plan</li>
                            <li>Some gyms offer first free classes for new members</li>
                            <li>You can register family members for classes from the class details</li>
                            <li>Check the gym's cancellation policy before registering</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Register early for popular classes as they fill up quickly. If a class is full, join the waitlist - you'll be notified if a spot becomes available.
                        </Typography>
                    </>
                ),
                tags: ['register', 'classes', 'booking', 'schedule', 'sign up', 'waitlist'],
                roles: ['regular']
            },
            {
                title: 'My Future Classes',
                content: (
                    <>
                        <Typography paragraph>
                            View and manage your upcoming class registrations:
                        </Typography>
                        <ol>
                            <li>Go to the "Classes" section</li>
                            <li>Click the calendar icon (📅) at the top of the page</li>
                            <li>This opens "My Future Classes" showing all your upcoming registrations</li>
                            <li>Each class displays:</li>
                            <ul>
                                <li>Date and time</li>
                                <li>Training name and type</li>
                                <li>Trainer name</li>
                                <li>Gym name</li>
                                <li>Waitlist status (if applicable)</li>
                            </ul>
                            <li>Click on any class to view full details</li>
                            <li>From the class details, you can cancel your registration</li>
                        </ol>
                        <Typography paragraph>
                            Cancellation guidelines:
                        </Typography>
                        <ul>
                            <li>Check the gym's cancellation policy (e.g., "cancel 2 hours before")</li>
                            <li>If you cancel before the deadline, your session is returned</li>
                            <li>Late cancellations may result in losing the session</li>
                            <li>Waitlisted classes can usually be cancelled anytime without penalty</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Regularly check your future classes to stay organized and avoid missing workouts. The list shows all your registrations across all gyms you're a member of.
                        </Typography>
                    </>
                ),
                tags: ['future classes', 'upcoming', 'my classes', 'schedule', 'registrations', 'cancel'],
                roles: ['regular']
            },
            {
                title: 'WOD Results & Leaderboard',
                content: (
                    <>
                        <Typography paragraph>
                            Submit your workout results and compete on the leaderboard:
                        </Typography>
                        <ol>
                            <li>After completing a WOD class, click on the class</li>
                            <li>Click "Submit Score" or "Add Result"</li>
                            <li>Enter your result based on the workout type:</li>
                            <ul>
                                <li><strong>Time:</strong> Enter completion time (e.g., 12:45)</li>
                                <li><strong>Reps:</strong> Enter total reps completed</li>
                                <li><strong>Weight:</strong> Enter maximum weight lifted</li>
                            </ul>
                            <li>Select your scaling level:</li>
                            <ul>
                                <li><strong>Rx:</strong> As prescribed (full workout)</li>
                                <li><strong>Sc:</strong> Scaled (modified version)</li>
                                <li><strong>Beg:</strong> Beginner modifications</li>
                            </ul>
                            <li>Add optional comments about your performance</li>
                            <li>Click "Submit" to save your result</li>
                        </ol>
                        <Typography paragraph>
                            Viewing leaderboards:
                        </Typography>
                        <ul>
                            <li>Click the trophy icon (🏆) to view daily leaderboards</li>
                            <li>Filter by Rx, Sc, or Beg to see results in each category</li>
                            <li>Your result is highlighted in the leaderboard</li>
                            <li>Previous scores are shown for comparison</li>
                            <li>View results from different classes on the same day</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Track your progress over time by consistently logging your results. The leaderboard is a great way to stay motivated and see how you compare with other members.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'info.main', mt: 1 }}>
                            Be honest with your scaling level - it helps you track real progress and compete fairly with others at similar levels.
                        </Typography>
                    </>
                ),
                tags: ['wod', 'results', 'leaderboard', 'score', 'rx', 'scaled', 'performance'],
                roles: ['regular']
            },
            {
                title: 'Managing Family Members',
                content: (
                    <>
                        <Typography paragraph>
                            Add and manage family members (e.g., children) in the system:
                        </Typography>
                        <ol>
                            <li>Go to your "Profile" section</li>
                            <li>Select the "Family" or "Children" tab</li>
                            <li>Click "Add Family Member"</li>
                            <li>Enter their details:</li>
                            <ul>
                                <li>Full name</li>
                                <li>Date of birth</li>
                                <li>Relationship (child, spouse, etc.)</li>
                            </ul>
                            <li>Save the family member</li>
                        </ol>
                        <Typography paragraph>
                            Using family member accounts:
                        </Typography>
                        <ul>
                            <li><strong>Class Registration:</strong> When viewing a class, you can register family members instead of yourself</li>
                            <li><strong>Plans:</strong> Assign plans to family members from your profile</li>
                            <li><strong>Check-ins:</strong> Gym staff can check in family members using their profiles</li>
                            <li><strong>Tracking:</strong> View each family member's attendance and activity</li>
                        </ul>
                        <Typography paragraph>
                            To register a family member for a class:
                        </Typography>
                        <ol>
                            <li>Click on the class you want to register for</li>
                            <li>Look for "Register Family Member" option</li>
                            <li>Select the family member from the dropdown</li>
                            <li>Ensure they have an active plan that covers the class type</li>
                            <li>Click "Register" to complete</li>
                        </ol>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Family member management makes it easy to handle multiple memberships under one account, perfect for parents managing their children's gym attendance.
                        </Typography>
                    </>
                ),
                tags: ['family', 'children', 'dependents', 'kids', 'family members'],
                roles: ['regular']
            },
            {
                title: 'Using the Waitlist',
                content: (
                    <>
                        <Typography paragraph>
                            Join waitlists when classes are full:
                        </Typography>
                        <ol>
                            <li>If a class shows "Full" or "0 spots available"</li>
                            <li>Click on the class to view details</li>
                            <li>Look for the "Join Waitlist" button</li>
                            <li>Click to add yourself to the waitlist</li>
                            <li>You'll receive a notification if a spot becomes available</li>
                        </ol>
                        <Typography paragraph>
                            How waitlists work:
                        </Typography>
                        <ul>
                            <li>When someone cancels, the first person on the waitlist is automatically registered</li>
                            <li>You'll receive an email/notification about your registration</li>
                            <li>The session will be deducted from your plan</li>
                            <li>Joining a waitlist doesn't use up a session</li>
                            <li>You can leave a waitlist anytime without penalty</li>
                        </ul>
                        <Typography paragraph>
                            Managing your waitlist positions:
                        </Typography>
                        <ul>
                            <li>View all your waitlist positions in "My Future Classes"</li>
                            <li>Waitlisted classes are marked with an orange "WAITLIST" badge</li>
                            <li>Click on a waitlisted class to remove yourself from the waitlist</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Don't give up on full classes! Many members cancel closer to class time, so joining the waitlist often results in getting a spot.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'warning.main', mt: 1 }}>
                            If you're automatically registered from a waitlist but can't attend, make sure to cancel according to the gym's policy to avoid losing your session.
                        </Typography>
                    </>
                ),
                tags: ['waitlist', 'full classes', 'waiting list', 'class capacity'],
                roles: ['regular']
            },
            {
                title: 'Viewing Training Plans & Programs',
                content: (
                    <>
                        <Typography paragraph>
                            Access personalized training plans from your gym:
                        </Typography>
                        <ol>
                            <li>Navigate to your "Profile" section</li>
                            <li>Select the "Training Plans" tab</li>
                            <li>View all training programs assigned to you by your trainer or gym</li>
                            <li>Click on a plan to see details:</li>
                            <ul>
                                <li>Workout schedule</li>
                                <li>Exercise descriptions</li>
                                <li>Sets, reps, and weights</li>
                                <li>Notes from your trainer</li>
                            </ul>
                            <li>Mark workouts as completed</li>
                            <li>Add notes about your performance</li>
                        </ol>
                        <Typography paragraph>
                            Training plan features:
                        </Typography>
                        <ul>
                            <li>View daily, weekly, or monthly programs</li>
                            <li>Track your progress over time</li>
                            <li>Receive updates when trainers modify plans</li>
                            <li>Access video demonstrations for exercises</li>
                            <li>Log weights, reps, and personal records</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Training plans help you stay consistent and work toward specific goals. Follow your coach's program and track your progress for best results.
                        </Typography>
                    </>
                ),
                tags: ['training plans', 'programs', 'workouts', 'coaching', 'personal training'],
                roles: ['regular']
            },
        ],
        trainers: [
            {
                title: 'Managing Classes',
                content: (
                    <>
                        <Typography paragraph>
                            As a trainer, you can manage training classes:
                        </Typography>
                        <ol>
                            <li>Navigate to the "Classes" section</li>
                            <li>View upcoming classes you're assigned to</li>
                            <li>Click "Add Training" to create a new class</li>
                            <li>Set the class details (date, time, capacity, type)</li>
                            <li>Click on a class to see attendees and details</li>
                            <li>Check in members as they arrive using the check-in button</li>
                            <li>View and manage the waitlist for full classes</li>
                        </ol>
                        <Typography paragraph>
                            To update or delete a class, click on it in the schedule and use the Edit or Delete buttons in the class details modal.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ mt: 2 }}>
                            You can also update or delete classes as needed, and mark attendees who didn't show up.
                        </Typography>
                    </>
                ),
                tags: ['classes', 'training', 'sessions', 'check-in', 'attendance'],
                roles: ['trainer', 'affiliate']
            },
            {
                title: 'Working with Members',
                content: (
                    <>
                        <Typography paragraph>
                            To manage your gym members:
                        </Typography>
                        <ol>
                            <li>Go to the "Members" section</li>
                            <li>View the list of all gym members</li>
                            <li>Search for specific members by name</li>
                            <li>Click on a member to view their profile</li>
                            <li>View their active plans, visit history, and statistics</li>
                            <li>Add training plans for individual members</li>
                            <li>Add notes and flags for members with special requirements</li>
                        </ol>
                        <Typography paragraph>
                            From the member details page, you can access:
                        </Typography>
                        <ul>
                            <li>Profile information</li>
                            <li>Training plans</li>
                            <li>Statistics</li>
                            <li>Purchase history</li>
                            <li>Visit history</li>
                            <li>Active plans</li>
                            <li>Credit information</li>
                        </ul>
                        <Typography paragraph>
                            Member notes with color-coded flags (red, yellow, green) help you keep track of important information about each member.
                        </Typography>
                    </>
                ),
                tags: ['members', 'athletes', 'clients', 'notes', 'flags'],
                roles: ['trainer', 'affiliate']
            },
            {
                title: 'Creating WODs',
                content: (
                    <>
                        <Typography paragraph>
                            Create and manage Workouts of the Day (WODs):
                        </Typography>
                        <ol>
                            <li>Navigate to the "Classes" section</li>
                            <li>Click "Add WOD"</li>
                            <li>Select a date for your WOD</li>
                            <li>Enter the WOD name, type, and description</li>
                            <li>Specify exercises, reps, and other details</li>
                            <li>Save your WOD</li>
                            <li>Click "Apply WOD" to add it to all training sessions with training type "WOD" on that day</li>
                        </ol>
                        <Typography paragraph>
                            You can also search and use previous WODs as templates to save time. The WOD will be visible to all users, who open class.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            When you click "Apply WOD," it will be applied to all classes, with training type "WOD", saving you time from manually updating each class.
                        </Typography>
                    </>
                ),
                tags: ['wod', 'workout of the day', 'programming', 'training plan'],
                roles: ['trainer', 'affiliate']
            },
            {
                title: 'Using Check-In Mode',
                content: (
                    <>
                        <Typography paragraph>
                            To use Check-In mode for class attendance:
                        </Typography>
                        <ol>
                            <li>Log in and select the "Check-In" role</li>
                            <li>You'll see today's classes listed</li>
                            <li>Click on a class to open the check-in screen</li>
                            <li>As members arrive, search for them by name</li>
                            <li>Click the check-in button next to their name</li>
                            <li>The system will verify if they have an active plan with available sessions</li>
                            <li>For drop-ins or non-members, you can create a special check-in</li>
                        </ol>
                        <Typography variant="subtitle2" sx={{ mt: 2 }}>
                            Check-in mode works well on tablets at the front desk. The system automatically reduces the session count from members' plans when they check in.
                        </Typography>
                        <Typography paragraph>
                            Members who are checked in will have a green check mark next to their name in the attendance list.
                        </Typography>
                    </>
                ),
                tags: ['check-in', 'attendance', 'front desk', 'class management'],
                roles: ['trainer', 'affiliate']
            },
            {
                title: 'Communicating with Members',
                content: (
                    <>
                        <Typography paragraph>
                            To communicate with your members:
                        </Typography>
                        <ol>
                            <li>Go to the "Messages" section</li>
                            <li>Click on "Send Message"</li>
                            <li>Choose recipient type (User, Group, or All Members)</li>
                            <li>Search for and select the recipient(s)</li>
                            <li>Compose your message with a subject and body</li>
                            <li>Click "Send" to deliver your message</li>
                        </ol>
                        <Typography paragraph>
                            Messages are delivered to users' email addresses and also appear in their Messages section. You can view your sent messages in the "Sent Messages" tab.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            You can easily send messages to all attendees of a class by going to the class details and clicking the "Send Message" button at the top of the attendees list.
                        </Typography>
                    </>
                ),
                tags: ['messages', 'communication', 'email', 'notifications'],
                roles: ['trainer', 'affiliate']
            },

        ],
        affiliateOwners: [
            {
                title: 'Affiliate FAQ',
                content: (
                    <>
                        <Typography paragraph>
                            Frequently asked questions about affiliate (club) accounts:
                        </Typography>
                        <ul>
                            <li><strong>How do I create a club account?</strong> Register first, then contact us at info@irontrack.ee so we can activate and configure your club account.</li>
                            <li><strong>Who can use affiliate features?</strong> Affiliate owner and trainer roles can access affiliate management tools based on permissions.</li>
                            <li><strong>Can regular users use Irontrack for free?</strong> Yes, regular user functionality is free.</li>
                            <li><strong>How do payments work?</strong> For online bank payments, set up Montonio integration in your affiliate settings.</li>
                            <li><strong>Is CAP integration available for everyone?</strong> CAP integration is available only for clubs with a valid CrossFit affiliate license in good standing.</li>
                        </ul>
                    </>
                ),
                tags: ['affiliate', 'faq', 'club account', 'pricing', 'cap', 'support'],
                roles: ['all']
            },
            {
                title: 'CrossFit CAP Integration',
                content: (
                    <>
                        <Typography paragraph>
                            Irontrack supports CrossFit Affiliate CAP Programming integration.
                            This feature helps eligible clubs connect CAP programming with their daily class workflow.
                        </Typography>
                        <Typography paragraph>
                            <strong>Who can access CAP integration?</strong><br />
                            Access is available only for clubs that hold a valid CrossFit affiliate license and are in good standing.
                        </Typography>
                        <Typography paragraph>
                            <strong>How to get started:</strong>
                        </Typography>
                        <ol>
                            <li>Create your account in Irontrack</li>
                            <li>Contact us at info@irontrack.ee with your club details</li>
                            <li>Our team verifies eligibility and helps with onboarding</li>
                        </ol>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            If you are unsure whether your club qualifies, send us your affiliate details and we will assist you.
                        </Typography>
                    </>
                ),
                tags: ['cap', 'crossfit', 'affiliate', 'integration', 'eligibility', 'good standing'],
                roles: ['all']
            },
            {
                title: 'Affiliate Help & Support',
                content: (
                    <>
                        <Typography paragraph>
                            Need help setting up or managing your affiliate? Our team can help with:
                        </Typography>
                        <ul>
                            <li>Affiliate owner account activation</li>
                            <li>Initial affiliate setup and configuration</li>
                            <li>Montonio payment integration setup</li>
                            <li>Contract and plan management guidance</li>
                            <li>CrossFit CAP integration onboarding</li>
                        </ul>
                        <Typography paragraph>
                            Contact us at <strong>info@irontrack.ee</strong> and include your club name plus a short description of what you need.
                        </Typography>
                    </>
                ),
                tags: ['affiliate', 'help', 'support', 'setup', 'montonio', 'cap'],
                roles: ['all']
            },
            {
                title: 'Managing Your Affiliate',
                content: (
                    <>
                        <Typography paragraph>
                            As an affiliate owner, you can manage your gym's information:
                        </Typography>
                        <ol>
                            <li>Go to "My Affiliate" in the main menu</li>
                            <li>View and edit basic information (name, address, contact details)</li>
                            <li>Upload your gym's logo</li>
                            <li>Manage trainer assignments by searching and adding trainers</li>
                            <li>Add first training free classes. If classes are chosen, first time register for those classes will be free.</li>
                            <li>Set payment holiday fees</li>
                        </ol>
                        <Typography paragraph>
                            Keep your information accurate to ensure proper operations and payments. Your affiliate's public page will display this information to potential members.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            A professional logo and complete contact information help make a good first impression on potential new members.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Add your affiliate Terms and conditions:
                        </Typography>
                        <ol>
                            <li>Go to "My Affiliate" in the main menu</li>
                            <li>Click on "Terms and conditions"</li>
                            <li>Enter your terms and conditions text</li>
                            <li>Click "Save" to apply changes</li>
                        </ol>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            User on their first registration will be asked to accept your terms and conditions. You can also edit or remove them at any time.
                        </Typography>
                    </>
                ),
                tags: ['affiliate', 'gym', 'box', 'business', 'location', 'settings'],
                roles: ['affiliate']
            },
            {
                title: 'Workflow - Automated Task Generation',
                content: (
                    <>
                        <Typography paragraph>
                            Workflow (premium feature) turns your member data into daily to-do tasks that help you
                            improve retention and revenue. You define rules, and every night at 04:10 the system
                            analyses your members and generates tasks - for example "contact a member who has not
                            trained in 21 days".
                        </Typography>
                        <Typography paragraph>
                            <strong>Getting started:</strong>
                        </Typography>
                        <ol>
                            <li>Go to "My Affiliate" and open the "Workflow" tab</li>
                            <li>Open the "Rules" tab and click "Add rule"</li>
                            <li>Choose a rule type, set its parameters (days, visits, plan, etc.) and priority</li>
                            <li>New tasks appear in the "Tasks" tab after the nightly run</li>
                        </ol>
                        <Typography paragraph>
                            <strong>Available rule types include:</strong>
                        </Typography>
                        <ul>
                            <li><strong>Member tenure:</strong> member has been a client for exactly X days</li>
                            <li><strong>Inactive member:</strong> last class was exactly X days ago</li>
                            <li><strong>No recent purchase:</strong> last plan purchase was exactly X days ago</li>
                            <li><strong>New member onboarding:</strong> member joined exactly X days ago - welcome call</li>
                            <li><strong>Visit frequency drop:</strong> visits dropped at least X% (last 30 days vs previous 30 days)</li>
                            <li><strong>Visit milestone:</strong> member crossed their Nth class within the last day - congratulate them</li>
                            <li><strong>Yearly visit milestone:</strong> member reached exactly N classes in the current calendar year (01.01 - 31.12)</li>
                            <li><strong>Contract ending:</strong> contract ends within X days</li>
                            <li><strong>Payment holiday ending:</strong> payment holiday ends within X days</li>
                            <li><strong>Unpaid contract:</strong> contract payment pending for at least X days</li>
                            <li><strong>Paying but not attending:</strong> active plan or contract, but last class was exactly X days ago</li>
                            <li><strong>Plan-specific inactivity:</strong> member of a selected plan (e.g. a base course) last trained exactly X days ago</li>
                            <li><strong>Membership anniversary:</strong> member has been a client for exactly N years</li>
                            <li><strong>First visit without purchase:</strong> first training was exactly X days ago and no plan bought</li>
                        </ul>
                        <Typography paragraph>
                            <strong>Working with tasks:</strong>
                        </Typography>
                        <ul>
                            <li>Each task shows the member's name, email and phone (with a copy button)</li>
                            <li>Use "Send email" to message the member directly through Irontrack</li>
                            <li>Click "Mark as done" and confirm to complete a task - completed tasks stay visible under the "Completed" filter</li>
                            <li>Filter tasks by priority or rule</li>
                        </ul>
                        <Typography paragraph>
                            <strong>Good to know:</strong>
                        </Typography>
                        <ul>
                            <li>The master switch "Workflow active" pauses ALL task generation, even for rules that are switched on</li>
                            <li>Each rule can be edited, switched on/off individually, or deleted (deleting a rule also removes its tasks)</li>
                            <li>You can create several rules of the same type, e.g. milestones at 10, 50 and 100 classes</li>
                            <li>Class registrations count as visits - check-in status does not matter</li>
                            <li>No duplicates: a member will not get a new task from the same rule while one is still open</li>
                            <li>Everything is per club: rules, tasks and member analysis never mix between affiliates</li>
                        </ul>
                    </>
                ),
                tags: ['workflow', 'tasks', 'rules', 'automation', 'retention', 'churn', 'premium'],
                roles: ['affiliate']
            },
            {
                title: 'Contracts',
                content: (
                    <>
                        <Typography paragraph>
                            Irontrack has three ways to start a monthly membership contract. One-time plans stay on the Plans page as usual — see "Managing Subscription Plans".
                        </Typography>
                        <ul>
                            <li><strong>Create a contract</strong> (Contracts page): you create it for a specific member, then send it for acceptance.</li>
                            <li><strong>Base contract</strong> (Plans page): members join and pay themselves. No action from you after you publish the offering.</li>
                            <li><strong>Contract request</strong> (Plans page): members send a request by email. You still create the contract afterwards.</li>
                        </ul>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                            Creating a contract for a member:
                        </Typography>
                        <ol>
                            <li>Navigate to the "Contracts" section under "My Affiliate"</li>
                            <li>Click "Add Contract" to create a new contract</li>
                            <li>Select a member from the search dropdown</li>
                            <li>Enter contract details:</li>
                            <ul>
                                <li>Training types</li>
                                <li>Contract Name</li>
                                <li>Payment amount</li>
                                <li>Payment interval</li>
                                <li>Payment day</li>
                                <li>Contract starting date (user plan will be active on that day)</li>
                            </ul>
                            <li>Set the contract content and click "Save Contract"</li>
                            <li>Click "Send" to send the contract to the member for approval</li>
                        </ol>
                        <Typography paragraph>
                            You can also create default contract templates by clicking "Add Default Contract". These templates save time when creating multiple similar contracts, and they are loaded automatically when you create a new base contract.
                        </Typography>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                            Payment holidays:
                        </Typography>
                        <ol>
                            <li>Review payment holiday requests in the contract details</li>
                            <li>Approve or decline requests using the green and red buttons</li>
                            <li>Manage payment holiday fees in your affiliate settings</li>
                        </ol>
                        <Typography paragraph>
                            Payment holidays allow members to temporarily pause their membership while they're away, but they won't be able to attend classes during this period.
                        </Typography>
                        <Typography paragraph>
                            How long a member must keep a contract before they can request termination is a club-wide setting (minimum contract validity months) on the Contracts page. Members see this period when they join a base contract.
                        </Typography>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                            Base contracts (self-service join):
                        </Typography>
                        <Typography paragraph>
                            A base contract is a ready-made membership that members join themselves. They pick a start date and a monthly payment day, accept the terms, and pay the first invoice. After that the contract is created and billed like any other monthly contract.
                        </Typography>
                        <ol>
                            <li>Go to the "Plans" section from the main menu</li>
                            <li>Click "Add Base Contract"</li>
                            <li>Fill in the details and save</li>
                        </ol>
                        <ul>
                            <li><strong>Name:</strong> Shown on the member's Buy Plans page (e.g. "Unlimited Monthly Membership").</li>
                            <li><strong>Contract type:</strong> Optional. Defaults to the name.</li>
                            <li><strong>Training types:</strong> Which classes this contract covers. Members can only register for matching classes.</li>
                            <li><strong>Monthly price:</strong> Recurring monthly fee.</li>
                            <li><strong>Contract period:</strong> 1 to 20 years. The end date is calculated from the member's start date.</li>
                            <li><strong>Payment description:</strong> Optional text that appears on the member's payment.</li>
                            <li><strong>Additional information:</strong> Short text on the offering card, above the contract text.</li>
                            <li><strong>Active:</strong> Only active base contracts are visible to members.</li>
                        </ul>
                        <Typography paragraph>
                            Payment day:
                        </Typography>
                        <ul>
                            <li><strong>Customer chooses:</strong> The member picks any day from 1 to 28 as the monthly due date.</li>
                            <li><strong>Fixed options:</strong> You choose which days are allowed (e.g. 1, 10, and 15). The member must pick one of those.</li>
                        </ul>
                        <Typography paragraph>
                            Days 29–31 are not used, so every month has a valid due date.
                        </Typography>
                        <Typography paragraph>
                            The first payment is calculated automatically from the member's start date and payment day, using the same rules as when you create a contract yourself:
                        </Typography>
                        <ul>
                            <li>Same calendar day: one full monthly fee.</li>
                            <li>Different days: a prorated amount for the days until the next payment day (and the next full month when that applies).</li>
                            <li><strong>Discount duration</strong> and <strong>discount percentage</strong> are optional. Fill in both or leave both empty. The discount applies to the first payment.</li>
                        </ul>
                        <Typography paragraph>
                            Members see the exact first amount, explanation, next payment date, and contract end date update live as they change dates.
                        </Typography>
                        <Typography paragraph>
                            The contract content field is the same as on Create Contract: a large text area that loads your latest default contract template when you create a new base contract. That text is copied onto each contract a member joins. Later edits to the base contract do not change contracts that have already been signed.
                        </Typography>
                        <Typography paragraph>
                            What the member does: opens Buy Plans, clicks "Join contract", chooses dates, accepts the contract and the 14-day withdrawal notice (counted from the start date), then pays. After a successful payment the contract is accepted, a signed copy is stored, and a user plan is created. Recurring billing then works like any other monthly contract: a payment link is emailed 10 days before each due date. If they start checkout but never pay, the unfinished contract is removed automatically after 48 hours.
                        </Typography>
                        <Typography paragraph>
                            Notices shown to the member: 14-day withdrawal from the start date, express request that the service may start then, obligation to pay, the contract remaining visible under My contracts, and that Stebby payments need to go through the club.
                        </Typography>
                        <ul>
                            <li>Click a base contract card on the Plans page to edit it. Changes apply only to new joins.</li>
                            <li>Uncheck Active to hide it from members without deleting it.</li>
                            <li>Delete only if no one should join this offering anymore. Existing signed contracts stay in place.</li>
                            <li>A member who already has an accepted or draft contract from the same base contract cannot join it again.</li>
                        </ul>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                            Contract requests (email only):
                        </Typography>
                        <Typography paragraph>
                            A contract request is an offering on Buy Plans that collects a start date, payment day, and optional extra questions, then emails the request to the club and a copy to the member. Nothing is stored in Irontrack and no contract is created until you create it yourself.
                        </Typography>
                        <ol>
                            <li>Go to the "Plans" section from the main menu</li>
                            <li>Click "Add Contract Request"</li>
                            <li>Fill in the details and save</li>
                        </ol>
                        <ul>
                            <li><strong>Name</strong> and <strong>monthly price:</strong> Shown on the member's card under "Contracts".</li>
                            <li><strong>Additional information:</strong> Short text on the offering card.</li>
                            <li><strong>Payment day mode:</strong> Customer chooses any day 1–28, or Fixed options you select.</li>
                        </ul>
                        <Typography paragraph>
                            Members are always asked for start date (1st–28th) and monthly payment day. You can add extra required questions:
                        </Typography>
                        <ul>
                            <li><strong>Text:</strong> Free-text answer.</li>
                            <li><strong>Radio:</strong> The member picks one option. Add at least two options; each needs a label and can have a short description.</li>
                        </ul>
                        <Typography paragraph>
                            When a member clicks "Request contract" and submits, the gym receives an email with their name, email, start date, payment day, and answers. A confirmation copy goes to the member. Then you create the actual contract on the Contracts page from that information and send it for acceptance.
                        </Typography>
                        <Typography paragraph>
                            Click a Contract Requests card on the Plans page to edit or delete the offering. Deleting it only hides the request form; it does not affect contracts you already created from earlier emails.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 2 }}>
                            Use a base contract when members should join and pay without your involvement. Use a contract request when you still want to review answers and create the contract yourself. Use Add Contract when you start the process for a specific member.
                        </Typography>
                    </>
                ),
                tags: ['contracts', 'memberships', 'agreements', 'payments', 'payment holidays', 'base contract', 'contract request', 'self-service', 'monthly', 'payment day', 'prorated', 'withdrawal', 'recurring'],
                roles: ['affiliate']
            },
            {
                title: 'Managing Subscription Plans',
                content: (
                    <>
                        <Typography paragraph>
                            Create and manage one-time membership plans (class packs and time-limited packs). Recurring monthly contracts — creating a contract for a member, base contracts, and contract requests — are documented under "Contracts".
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                            Creating a New Plan:
                        </Typography>
                        <ol>
                            <li>Go to the "Plans" section from the main menu</li>
                            <li>Click "Add New Plan" button</li>
                            <li>Fill in the plan details:</li>
                        </ol>
                        
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
                            Basic Information:
                        </Typography>
                        <ul>
                            <li><strong>Plan Name:</strong> Give your plan a descriptive name (e.g., "Unlimited Monthly", "10 Class Pack")</li>
                            <li><strong>Price:</strong> Set the price in euros (e.g., 50.00)</li>
                            <li><strong>Validity Days:</strong> Number of days the plan is valid after purchase (e.g., 30 for monthly, 365 for yearly)</li>
                            <li><strong>Sessions:</strong> Number of classes included
                                <ul>
                                    <li>Enter a specific number (e.g., 8, 12, 20)</li>
                                    <li>Set to <strong>9999</strong> for unlimited sessions</li>
                                </ul>
                            </li>
                            <li><strong>Additional Data:</strong> Optional description or terms for the plan</li>
                        </ul>

                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
                            Training Types:
                        </Typography>
                        <Typography paragraph>
                            Select which training types are included in this plan. Members can only register for classes that match their plan's training types.
                        </Typography>
                        <ul>
                            <li><strong>All classes:</strong> Access to every training type at your gym</li>
                            <li><strong>Specific types:</strong> WOD, Weightlifting, Cardio, Rowing, Gymnastics, Open Gym, Kids, Basic, Hyrox, Circuit, Strength, Hybrid, Mobility</li>
                            <li>You can select multiple training types for mixed plans</li>
                        </ul>

                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
                            Advanced Settings:
                        </Typography>
                        <ul>
                            <li><strong>Active Status:</strong>
                                <ul>
                                    <li>✓ Checked: Plan is visible in the store for members to purchase</li>
                                    <li>✗ Unchecked: Plan is hidden from store but you can still manually assign it to members</li>
                                    <li>Useful for custom plans, promotions, or discontinued plans</li>
                                </ul>
                            </li>
                            <li><strong>First Buy Only:</strong>
                                <ul>
                                    <li>When enabled, members can only purchase this plan once (first purchase only)</li>
                                    <li>Great for "New Member Special" or "Trial" plans</li>
                                    <li>System tracks if member already purchased this plan</li>
                                </ul>
                            </li>
                            <li><strong>Special Price:</strong>
                                <ul>
                                    <li>Mark plans as promotional offers</li>
                                    <li>Displays with a red "PROMO" tag at the top-right corner</li>
                                    <li>Appears first in the plans list with special highlighting</li>
                                    <li>Useful for limited-time offers or seasonal promotions</li>
                                </ul>
                            </li>
                            <li><strong>Assign To:</strong>
                                <ul>
                                    <li>Set to "1" to mark this plan for specific user assignment</li>
                                    <li>Shows a "Assigned to Users" badge on the plan card</li>
                                </ul>
                            </li>
                        </ul>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                            Plan Gallery System:
                        </Typography>
                        <Typography paragraph>
                            Organize related plans into galleries for better presentation:
                        </Typography>
                        <ul>
                            <li><strong>Gallery Plans:</strong> Select multiple related plans to group together
                                <ul>
                                    <li>Example: Group "1 Month", "3 Months", and "6 Months" versions of the same plan</li>
                                    <li>Only active plans can be added to galleries</li>
                                    <li>Plans in a gallery display in a carousel view</li>
                                </ul>
                            </li>
                            <li><strong>Main Plan:</strong> The plan containing the gallery becomes the main plan
                                <ul>
                                    <li>Shows a "Gallery Main Plan" badge with count</li>
                                    <li>Desktop view shows 3 plans at once (left, center, right)</li>
                                    <li>Mobile view shows one plan at a time with navigation arrows</li>
                                </ul>
                            </li>
                            <li><strong>Gallery Organization:</strong>
                                <ul>
                                    <li>Galleries with special price plans appear first</li>
                                    <li>Individual plans are sorted by price (special price first)</li>
                                    <li>Navigation dots show position in gallery</li>
                                </ul>
                            </li>
                        </ul>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                            Plan Display & Organization:
                        </Typography>
                        <ul>
                            <li><strong>Plan Cards show:</strong>
                                <ul>
                                    <li>Plan ID (for reference)</li>
                                    <li>Plan name</li>
                                    <li>Price in euros</li>
                                    <li>Validity period</li>
                                    <li>Number of sessions or "Unlimited sessions"</li>
                                    <li>Active/Inactive status (green or red dot)</li>
                                    <li>Training types as colored chips</li>
                                    <li>Special badges (Special Price, Assigned to Users, Gallery Main Plan)</li>
                                </ul>
                            </li>
                            <li><strong>Plans are organized into sections:</strong>
                                <ol>
                                    <li>Special Offers (special price plans with 🔥 icon)</li>
                                    <li>Plan Galleries (grouped plans in carousels)</li>
                                    <li>Individual Plans (standalone plans)</li>
                                </ol>
                            </li>
                        </ul>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                            Editing Plans:
                        </Typography>
                        <ul>
                            <li>Click on any plan card to edit its details</li>
                            <li>All fields can be modified</li>
                            <li>Changes take effect immediately for new purchases</li>
                            <li>Existing user plans are not affected by changes</li>
                            <li>You can change gallery membership by editing the gallery plans selection</li>
                        </ul>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                            Best Practices:
                        </Typography>
                        <ul>
                            <li>Use clear, descriptive names that explain the plan value</li>
                            <li>Group similar plans (different durations) into galleries</li>
                            <li>Set validity days to match billing cycles (30, 90, 365)</li>
                            <li>Use "All classes" for premium unlimited plans</li>
                            <li>Create specific training type plans for specialty programs</li>
                            <li>Inactive plans for grandfathered pricing or seasonal offers</li>
                            <li>Use "First Buy Only" for trial offers to prevent repeat purchases</li>
                            <li>Mark promotions with "Special Price" for visibility</li>
                        </ul>

                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 2 }}>
                            💡 Tip: Members can only register for classes that match the training types included in their active plans. Make sure your plan structure aligns with your class offerings.
                        </Typography>
                        
                        <Typography variant="subtitle2" sx={{ color: 'warning.main', mt: 1 }}>
                            ⚠️ Important: Setting sessions to 9999 creates an unlimited plan. Members with unlimited plans can attend any number of classes within the validity period.
                        </Typography>

                        <Typography variant="subtitle2" sx={{ color: 'info.main', mt: 1 }}>
                            📊 Plan IDs are unique identifiers shown on each card. Use these IDs when referencing specific plans in support or financial reports.
                        </Typography>
                    </>
                ),
                tags: ['plans', 'subscriptions', 'pricing', 'memberships', 'gallery', 'special price', 'unlimited', 'training types'],
                roles: ['affiliate']
            },
            {
                title: 'Managing Members',
                content: (
                    <>
                        <Typography paragraph>
                            To manage your gym members:
                        </Typography>
                        <ol>
                            <li>Navigate to the "Members" section</li>
                            <li>View all members associated with your affiliate</li>
                            <li>Search for specific members by name</li>
                            <li>Click on a member to view their complete profile</li>
                            <li>From the member profile, you can:</li>
                            <ul>
                                <li>View personal information</li>
                                <li>Assign training plans</li>
                                <li>View statistics and visit history</li>
                                <li>Manage active plans</li>
                                <li>Add credit</li>
                                <li>View contracts and transactions</li>
                                <li>Add contact notes</li>
                            </ul>
                        </ol>
                        <Typography paragraph>
                            If someone isn't already a member, you can add them by searching for their account and clicking "Add Member". You can also search potential users and add them to your affiliate.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Use the Notes feature with color-coded flags to keep track of important information about each member, such as medical conditions, specific goals, or preferences.
                        </Typography>
                    </>
                ),
                tags: ['members', 'users', 'management', 'profiles'],
                roles: ['affiliate']
            },
            {
                title: 'Financial Management',
                content: (
                    <>
                        <Typography paragraph>
                            Monitor and manage your gym's finances:
                        </Typography>
                        <ol>
                            <li>Navigate to the "Finance" section under "My Affiliate"</li>
                            <li>View the Orders History tab for all purchase records</li>
                            <li>Check the Finance tab for revenue summaries and member statistics</li>
                            <li>Review the Transactions tab for payment details</li>
                            <li>Filter by date ranges and search for specific transactions</li>
                            <li>Navigate to "Unpaid Users" to view members with pending payments</li>
                        </ol>
                        <Typography paragraph>
                            The Finance section provides important metrics such as:
                        </Typography>
                        <ul>
                            <li>Total revenue</li>
                            <li>Monthly comparison</li>
                            <li>New members</li>
                            <li>Active contracts</li>
                            <li>Payment due dates</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Regularly reviewing your financial data helps you understand revenue patterns, identify growth opportunities, and plan for the future of your business.
                        </Typography>
                    </>
                ),
                tags: ['finance', 'revenue', 'payments', 'orders', 'transactions', 'money'],
                roles: ['affiliate']
            },
            {
                title: 'Credit System',
                content: (
                    <>
                        <Typography paragraph>
                            Manage credit for your members:
                        </Typography>
                        <ol>
                            <li>Navigate to a member's profile in the Members section</li>
                            <li>Select the "Credit" tab</li>
                            <li>View current credit balance</li>
                            <li>Add credit by clicking "Add Credit"</li>
                            <li>Enter the amount and a description</li>
                            <li>Click "Apply" to add the credit</li>
                            <li>View credit history to track all additions and usage</li>
                        </ol>
                        <Typography paragraph>
                            Members can use credit for purchasing plans or paying for classes. Credit can be given as a refund, promotion, or compensation.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            The credit system is useful for handling refunds, offering promotions, or rewarding loyal members. Members can apply their credit during checkout when purchasing plans.
                        </Typography>
                    </>
                ),
                tags: ['credit', 'balance', 'funds', 'payments', 'refunds'],
                roles: ['affiliate']
            },
            {
                title: 'Montonio Payment Integration',
                content: (
                    <>
                        <Typography paragraph>
                            Set up Montonio payment processing for your affiliate to accept online bank payments from members:
                        </Typography>
                        
                        <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                            Getting Your Montonio API Keys
                        </Typography>
                        <ol>
                            <li>Register your business at <Link href="https://partner.montonio.com" target="_blank" rel="noopener">partner.montonio.com</Link></li>
                            <li>Complete the verification process</li>
                            <li>Navigate to Settings → API Keys in the Montonio Partner portal</li>
                            <li>Use 2FA or eID to view your keys</li>
                            <li>Copy both the Access Key (UUID format) and Secret Key</li>
                        </ol>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                            Adding Keys to Irontrack
                        </Typography>
                        <ol>
                            <li>Go to "My Affiliate" in the main menu</li>
                            <li>Click the "Montonio API" button</li>
                            <li>Paste your Access Key in the first field</li>
                            <li>Paste your Secret Key in the second field (will be hidden for security)</li>
                            <li>Click "Save Keys" to store them securely</li>
                        </ol>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                            Activating Payment Methods
                        </Typography>
                        <Typography paragraph>
                            In the Montonio Partner portal, activate the payment methods you want to offer:
                            bank payment, card payment, or BLIK. The same payment methods must also be enabled
                            in Irontrack under your affiliate settings. If a payment method is active in only one
                            place, it may not appear at checkout or payments may fail.
                        </Typography>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                            Managing Payment Methods
                        </Typography>
                        <Typography paragraph>
                            The club manages payment methods in two places. First activate every payment method
                            you want to accept in the Montonio Partner portal. Then go to My Affiliate in Irontrack,
                            edit the affiliate settings, and enable the corresponding payment methods. Irontrack
                            only displays methods that are permitted by the affiliate settings, but enabling a
                            method in Irontrack does not activate it in Montonio.
                        </Typography>
                        <ol>
                            <li>Open the Montonio Partner portal and select your store.</li>
                            <li>Activate the bank and card payment products that your club wants to accept.</li>
                            <li>In Irontrack, go to My Affiliate and open the affiliate settings.</li>
                            <li>Set the correct payment country and currency for the club.</li>
                            <li>Enable Bank payment, Card payment, or BLIK as required and save the settings.</li>
                        </ol>
                        <Typography paragraph>
                            Changes in Montonio may not be available immediately while the payment product is
                            being reviewed or activated. Do not offer a method in Irontrack until Montonio shows
                            it as active for the same store and API keys.
                        </Typography>
                        <ul>
                            <li><strong>Bank payment:</strong> Must be active in Montonio and enabled in Irontrack.</li>
                            <li><strong>Card payment:</strong> Must be active in Montonio and enabled in Irontrack.</li>
                            <li><strong>BLIK:</strong> Requires PLN, must be active in Montonio, and must be enabled separately in Irontrack.</li>
                            <li><strong>MobilePay:</strong> Requires card payments, payment country Finland (FI), and EUR. It has no separate Irontrack switch.</li>
                        </ul>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                            MobilePay
                        </Typography>
                        <Typography paragraph>
                            MobilePay does not have a separate activation switch in Irontrack. It is shown as a
                            separate checkout option automatically when card payments are enabled, the affiliate
                            payment country is Finland (FI), and the payment currency is EUR. When a customer
                            selects MobilePay, Irontrack sends it to Montonio as the MobilePay payment method.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            If MobilePay is not visible at checkout, verify that card payments are enabled in both
                            Montonio and Irontrack and that the affiliate payment country and currency are set to
                            Finland and EUR.
                        </Typography>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                            Security Information
                        </Typography>
                        <Typography paragraph>
                            Your Montonio Secret Key is encrypted using AES-256 encryption before being stored in the database. 
                            This ensures maximum security for your payment credentials. The key is only decrypted when needed 
                            to process payments and is never visible in the user interface after saving.
                        </Typography>


                        <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                            Managing Your Keys
                        </Typography>
                        <Typography paragraph>
                            You can update or delete your Montonio API keys at any time:
                        </Typography>
                        <ul>
                            <li><strong>Update:</strong> Enter new keys and click "Update Keys" to replace existing credentials</li>
                            <li><strong>Delete:</strong> Click "Delete" to remove payment processing (will require confirmation)</li>
                        </ul>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                            Troubleshooting
                        </Typography>
                        <Typography paragraph>
                            Common issues and solutions:
                        </Typography>
                        <ul>
                            <li><strong>Payment fails:</strong> Verify your keys are correct and active in Montonio Partner portal. Also ensure your bank details (IBAN, account information) are correctly configured in Montonio - incorrect bank details will prevent payments from being processed.</li>
                            <li><strong>Payment method is missing:</strong> Check that it is active for the store in Montonio, enabled in Irontrack, and compatible with the selected payment country and currency.</li>
                            <li><strong>Wrong methods appear:</strong> Review the payment country, currency, and method switches under My Affiliate, then save the settings again.</li>
                            <li><strong>Can't see API button:</strong> Ensure you're logged in as the affiliate owner with proper role</li>
                            <li><strong>Keys won't save:</strong> Check that both Access Key and Secret Key are provided</li>
                        </ul>

            
                    </>
                ),
                tags: ['montonio', 'payment', 'api', 'integration', 'bank payment', 'card payment', 'mobilepay', 'finland', 'online payment', 'payment gateway', 'setup', 'configuration'],
                roles: ['affiliate']
            },
            {
                title: 'Analytics Dashboard',
                content: (
                    <>
                        <Typography paragraph>
                            Monitor your gym's performance with the Analytics Dashboard:
                        </Typography>
                        <ol>
                            <li>Go to "My Affiliate" and select the "Analytics" tab</li>
                            <li>View key metrics including:</li>
                            <ul>
                                <li>Member growth over time</li>
                                <li>Class attendance statistics</li>
                                <li>Revenue trends</li>
                                <li>Popular class types</li>
                                <li>Peak usage times</li>
                            </ul>
                            <li>Filter data by date ranges</li>
                            <li>Export reports for further analysis</li>
                        </ol>
                        <Typography paragraph>
                            The analytics dashboard helps you make informed decisions about class scheduling, pricing, and marketing efforts by identifying trends and patterns.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Pay special attention to the "At Risk Members" section to identify members who might be considering leaving, allowing you to proactively reach out to them.
                        </Typography>
                    </>
                ),
                tags: ['analytics', 'statistics', 'reports', 'metrics', 'performance', 'trends'],
                roles: ['affiliate']
            },
            {
                title: 'Managing Classes',
                content: (
                    <>
                        <Typography paragraph>
                            This section covers day-to-day class management for club owners (without programming details):
                        </Typography>
                        <ol>
                            <li>Navigate to the "Classes" section</li>
                            <li>Click "Add Training" to create a new class time slot</li>
                            <li>Set date, time, capacity, and assign a trainer</li>
                            <li>Click on existing classes to edit details, capacity, trainer, or registration settings</li>
                            <li>Open a class to view attendees, check-ins, waitlist, and leaderboard</li>
                            <li>Use filters to quickly find relevant classes and class types</li>
                        </ol>
                        <Typography paragraph>
                            You can view classes in both day and week mode, navigate between weeks, and manage the schedule from one place.
                        </Typography>
                        <Typography paragraph>
                            <strong>Common owner questions:</strong>
                        </Typography>
                        <ul>
                            <li><strong>How do I stop a repeating class series?</strong> Open the last occurrence you want to keep, edit it, set repeat to "No", then save.</li>
                            <li><strong>How do I remove only one class occurrence?</strong> Delete that single class; the future repeated classes remain.</li>
                            <li><strong>How do I update all future repeated classes?</strong> Edit one class in the series and enable "Apply For All Future Trainings".</li>
                            <li><strong>How do I handle full classes?</strong> Use waitlist management from class details and promote members when a spot opens.</li>
                            <li><strong>Can I still edit class info after members register?</strong> Yes, but communicate major changes (time/trainer/type) to members in advance.</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Best practice: keep your week schedule stable first (times, trainers, capacity), then do workout programming as a separate step.
                        </Typography>
                    </>
                ),
                tags: ['classes', 'schedule', 'repeating classes', 'attendance', 'waitlist', 'club owner faq'],
                roles: ['affiliate']
            },
            {
                title: 'WOD Programming',
                content: (
                    <>
                        <Typography paragraph>
                            Use WOD Programming when you want to create or edit the daily workout content for your WOD classes.
                        </Typography>
                        <ol>
                            <li>Open "Classes" and switch to the WOD view</li>
                            <li>Add or edit the day&apos;s WOD (RX / Intermediate / Beginner and optional sections)</li>
                            <li>Save the WOD for that date</li>
                            <li>Click "Apply WOD" to push it to all class sessions with training type "WOD" on that day</li>
                        </ol>
                        <Typography paragraph>
                            <strong>Common owner questions:</strong>
                        </Typography>
                        <ul>
                            <li><strong>What does "Apply WOD" do?</strong> It updates all WOD-type classes for that date so coaches and members see the same workout.</li>
                            <li><strong>Can I move a WOD to another day?</strong> Yes. In weekly mode you can move/swap days using drag-and-drop.</li>
                            <li><strong>How do day/week views affect programming?</strong> They are only viewing modes; your saved content stays on the selected date.</li>
                            <li><strong>Where does Competition Info go?</strong> Competition content is handled separately from base class programming (see CAP / Compete guidance).</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Recommended flow: save WOD first -> verify day/date -> apply when ready for member-facing class schedule.
                        </Typography>
                    </>
                ),
                tags: ['wod', 'programming', 'apply wod', 'rx', 'intermediate', 'beginner'],
                roles: ['affiliate']
            },
            {
                title: 'CAP & Compete Workflow',
                content: (
                    <>
                        <Typography paragraph>
                            CAP workflow is separate from normal class management and is available for eligible affiliates.
                        </Typography>
                        <ol>
                            <li>Review tracks and apply needed parts for each day</li>
                            <li>Use Compete workflow separately from Class programming when needed</li>
                            <li>Apply compete content only when you are ready to publish it to class sessions</li>
                        </ol>
                        <Typography paragraph>
                            <strong>Common owner questions:</strong>
                        </Typography>
                        <ul>
                            <li><strong>Does CAP replace all classes automatically?</strong> No. You decide what and when to apply.</li>
                            <li><strong>Is Compete tied to base Class programming?</strong> It is managed separately and can be moved/applied independently.</li>
                            <li><strong>Can I run normal class programming without CAP?</strong> Yes, CAP is optional and your regular workflow still works.</li>
                            <li><strong>Who can use CAP integration?</strong> Clubs with valid CrossFit affiliate eligibility and enabled CAP access.</li>
                            <li><strong>Can I upload CAP files myself?</strong> Not currently. Send files/support request to Irontrack and we handle the upload.</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Keep CAP/Compete steps separate from class scheduling to avoid accidental overwrites.
                        </Typography>
                    </>
                ),
                tags: ['cap', 'compete', 'crossfit cap', 'workflow', 'affiliate'],
                roles: ['affiliate']
            },
            {
                title: 'Assigning Plans to Users',
                content: (
                    <>
                        <Typography paragraph>
                            To assign a plan to a user directly:
                        </Typography>
                        <ol>
                            <li>Go to the member's profile in the "Members" section</li>
                            <li>Click on the "Active Plans" tab</li>
                            <li>Click "Add Plan" button</li>
                            <li>Select one of your affiliate plans from the dropdown</li>
                            <li>Click "Assign Plan"</li>
                            <li>The plan will be immediately activated for the user</li>
                        </ol>
                        <Typography paragraph>
                            This is useful for complementary plans, promotions, or when handling payments outside the system. The plan will immediately appear in the user's active plans.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ mt: 2 }}>
                            Note: You can also edit existing plans to extend end dates or add sessions if needed.
                        </Typography>
                    </>
                ),
                tags: ['assign plan', 'membership', 'complement', 'add plan'],
                roles: ['affiliate']
            },
            {
                title: 'Managing Contact Groups',
                content: (
                    <>
                        <Typography paragraph>
                            Create and manage contact groups for messaging:
                        </Typography>
                        <ol>
                            <li>Go to the "Messages" section</li>
                            <li>Select the "Groups" tab</li>
                            <li>Click "Add Group" to create a new group</li>
                            <li>Enter a group name</li>
                            <li>Search for and add members to the group</li>
                            <li>Save the group</li>
                            <li>Use these groups when sending messages to quickly reach specific sets of members</li>
                        </ol>
                        <Typography paragraph>
                            Groups help you organize your communication efficiently. Create groups based on training preferences, schedules, or any other criteria that makes sense for your gym.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Example groups might include "Competition Team," "Morning Classes," "Beginners," or "Weightlifting Enthusiasts."
                        </Typography>
                    </>
                ),
                tags: ['groups', 'contacts', 'distribution lists', 'batch messaging'],
                roles: ['affiliate']
            },
            {
                title: 'Using Analytics for Business Decisions',
                content: (
                    <>
                        <Typography paragraph>
                            The Analytics Dashboard provides valuable insights for your business:
                        </Typography>
                        <ul>
                            <li><strong>Membership Trends:</strong> Track growth over time and identify seasonal patterns</li>
                            <li><strong>Class Capacity:</strong> See which classes are consistently full or underutilized</li>
                            <li><strong>Revenue Analysis:</strong> Understand your income sources and payment patterns</li>
                            <li><strong>Activity Heatmap:</strong> Discover peak times for scheduling classes</li>
                            <li><strong>At-Risk Members:</strong> Identify members who have reduced their attendance</li>
                            <li><strong>Contract Expirations:</strong> Plan renewals and marketing campaigns</li>
                        </ul>
                        <Typography paragraph>
                            Use these insights to make data-driven decisions about:
                        </Typography>
                        <ul>
                            <li>Adjusting class schedules to match demand</li>
                            <li>Pricing strategies for different plans</li>
                            <li>Staff allocation during peak times</li>
                            <li>Targeted retention campaigns</li>
                            <li>Marketing efforts for underutilized services</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Regularly review these analytics to stay proactive rather than reactive in your business management.
                        </Typography>
                    </>
                ),
                tags: ['analytics', 'business intelligence', 'decision making', 'planning', 'optimization'],
                roles: ['affiliate']
            },
            {
                title: 'Gift Cards Management',
                content: (
                    <>
                        <Typography paragraph>
                            Create and manage gift cards for your gym:
                        </Typography>
                        <ol>
                            <li>Navigate to "My Affiliate" and select the "Gift Cards" tab</li>
                            <li>Click "Add Gift Card" to create a new gift card</li>
                            <li>Fill in the details:</li>
                            <ul>
                                <li>Gift card name</li>
                                <li>Amount (value in euros)</li>
                                <li>Valid until date</li>
                                <li>Code: Either auto-generate or enter manually (minimum 3 characters)</li>
                            </ul>
                            <li>Save the gift card</li>
                            <li>Share the code with the recipient</li>
                        </ol>
                        <Typography paragraph>
                            Managing gift cards:
                        </Typography>
                        <ul>
                            <li><strong>Active:</strong> View all available gift cards</li>
                            <li><strong>Redeemed:</strong> See used gift cards with user information and redemption date</li>
                            <li><strong>Expired:</strong> View expired gift cards that are no longer valid</li>
                            <li>Edit gift card details by clicking on them (only active cards can be edited)</li>
                            <li>Delete gift cards that haven't been redeemed yet</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Gift cards are a great way to offer promotions, rewards, or gift options for your members. Once redeemed, they cannot be edited or deleted.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'warning.main', mt: 1 }}>
                            Make sure to set appropriate expiry dates for your gift cards. Expired cards cannot be used by members.
                        </Typography>
                    </>
                ),
                tags: ['gift cards', 'promotions', 'vouchers', 'rewards', 'credits'],
                roles: ['affiliate']
            },
            {
                title: 'Notifications System',
                content: (
                    <>
                        <Typography paragraph>
                            Irontrack currently uses notifications primarily for announcements.
                        </Typography>
                        <ol>
                            <li>Go to "My Affiliate" and select the "Notifications" tab</li>
                            <li>Click "Add Notification" and publish an active notification</li>
                            <li>Members receive it in-app, and push delivery is attempted if their device is subscribed</li>
                            <li>Enable or disable notifications as needed</li>
                        </ol>
                        <Typography paragraph>
                            Current scope: announcements. Additional notification types (for example class changes or payment reminders) can be added later.
                        </Typography>
                        <ul>
                            <li>iOS users should use the installed Home Screen app and allow notifications in iOS settings</li>
                            <li>Android badge/number behavior can depend on launcher and may appear under browser channel</li>
                            <li>If a member selected No before, they must re-enable notifications in device/browser settings</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Automated notifications help maintain member engagement and reduce churn by keeping them informed about important dates and events.
                        </Typography>
                    </>
                ),
                tags: ['notifications', 'automation', 'alerts', 'reminders', 'engagement', 'push', 'consent'],
                roles: ['affiliate']
            },
            {
                title: 'Trainer Schedule Management',
                content: (
                    <>
                        <Typography paragraph>
                            Manage trainer schedules and assignments:
                        </Typography>
                        <ol>
                            <li>Navigate to "My Affiliate" and select "Trainer Schedule"</li>
                            <li>View the weekly schedule with all trainers and their assigned classes</li>
                            <li>To assign a trainer to classes:</li>
                            <ul>
                                <li>Go to "Classes" section</li>
                                <li>Click "Add Coaches" button</li>
                                <li>Select a date</li>
                                <li>Choose the trainer from the dropdown</li>
                                <li>Select one or more classes</li>
                                <li>Click "Assign" to save</li>
                            </ul>
                        </ol>
                        <Typography paragraph>
                            The trainer schedule displays:
                        </Typography>
                        <ul>
                            <li>Color-coded view for each trainer</li>
                            <li>Daily and weekly schedule overview</li>
                            <li>Class times, types, and assigned trainers</li>
                            <li>Capacity and attendance information</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Use the color legend to quickly identify which trainer is assigned to which classes. This helps with scheduling and workload distribution.
                        </Typography>
                    </>
                ),
                tags: ['trainer', 'schedule', 'assignment', 'coaches', 'calendar'],
                roles: ['affiliate']
            },
            {
                title: 'Reports & Data Export',
                content: (
                    <>
                        <Typography paragraph>
                            Generate and export various reports:
                        </Typography>
                        <ol>
                            <li>Navigate to "My Affiliate" and select "Reports"</li>
                            <li>Choose the report type:</li>
                            <ul>
                                <li>Financial reports (revenue, transactions, contracts by price)</li>
                                <li>Membership reports (active members, new members, churn)</li>
                                <li>Attendance reports (class attendance, check-ins)</li>
                                <li>Contract reports (active contracts, renewals)</li>
                            </ul>
                            <li>Select date range for the report</li>
                            <li>Apply any additional filters</li>
                            <li>Click "Generate Report"</li>
                            <li>Export to Excel or PDF format</li>
                        </ol>
                        <Typography paragraph>
                            Reports help you:
                        </Typography>
                        <ul>
                            <li>Track business performance over time</li>
                            <li>Identify trends and patterns</li>
                            <li>Make data-driven decisions</li>
                            <li>Prepare for tax reporting</li>
                            <li>Share insights with stakeholders</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Regular reporting helps you stay on top of your business metrics and identify opportunities for improvement or growth.
                        </Typography>
                    </>
                ),
                tags: ['reports', 'export', 'data', 'analytics', 'excel', 'pdf'],
                roles: ['affiliate']
            },
            {
                title: 'Competitions Management',
                content: (
                    <>
                        <Typography paragraph>
                            Create and manage competitions for your gym:
                        </Typography>
                        <ol>
                            <li>Go to "My Affiliate" and select "Competitions"</li>
                            <li>Click "Create Competition" to start a new competition</li>
                            <li>Fill in competition details:</li>
                            <ul>
                                <li>Competition name and description</li>
                                <li>Start and end dates</li>
                                <li>Registration deadline</li>
                                <li>Divisions/categories</li>
                                <li>Entry fee (if applicable)</li>
                            </ul>
                            <li>Add workouts to the competition schedule</li>
                            <li>Set scoring type (time, reps, weight, etc.)</li>
                            <li>Publish the competition for member registration</li>
                        </ol>
                        <Typography paragraph>
                            During the competition:
                        </Typography>
                        <ul>
                            <li>View registered participants</li>
                            <li>Input scores as workouts are completed</li>
                            <li>Monitor real-time leaderboards</li>
                            <li>Update workout details if needed</li>
                            <li>Send announcements to participants</li>
                        </ul>
                        <Typography paragraph>
                            After the competition:
                        </Typography>
                        <ul>
                            <li>Finalize results and announce winners</li>
                            <li>Export competition data</li>
                            <li>Share results with participants</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Competitions are a great way to engage your community, attract new members, and create exciting events at your gym.
                        </Typography>
                    </>
                ),
                tags: ['competitions', 'events', 'leaderboard', 'contests', 'tournaments'],
                roles: ['affiliate']
            },
            {
                title: 'Message Templates',
                content: (
                    <>
                        <Typography paragraph>
                            Create reusable message templates for common communications:
                        </Typography>
                        <ol>
                            <li>Navigate to "My Affiliate" and select "Message Templates"</li>
                            <li>Click "Add Template" to create a new template</li>
                            <li>Enter template details:</li>
                            <ul>
                                <li>Template name (for your reference)</li>
                                <li>Subject line</li>
                                <li>Message body</li>
                                <li>Use placeholders for personalization (e.g., [Name], [Plan], [Date])</li>
                            </ul>
                            <li>Save the template</li>
                        </ol>
                        <Typography paragraph>
                            Using templates:
                        </Typography>
                        <ul>
                            <li>When composing a message, select "Use Template"</li>
                            <li>Choose from your saved templates</li>
                            <li>The template content will populate the message</li>
                            <li>Placeholders will be automatically replaced with member-specific information</li>
                            <li>Edit the message if needed before sending</li>
                        </ul>
                        <Typography paragraph>
                            Common template examples:
                        </Typography>
                        <ul>
                            <li>Welcome messages for new members</li>
                            <li>Plan expiry reminders</li>
                            <li>Class cancellation notifications</li>
                            <li>Payment reminders</li>
                            <li>Birthday wishes</li>
                            <li>Re-engagement messages</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Templates save time and ensure consistent, professional communication with your members. Use placeholders to keep messages personal while maintaining efficiency.
                        </Typography>
                    </>
                ),
                tags: ['templates', 'messages', 'automation', 'communication', 'personalization'],
                roles: ['affiliate']
            },
            {
                title: 'First Free Training Settings',
                content: (
                    <>
                        <Typography paragraph>
                            Configure which classes are free for first-time users:
                        </Typography>
                        <ol>
                            <li>Go to "My Affiliate" section</li>
                            <li>Click on the "My Affiliate" tab</li>
                            <li>Scroll to "First Free Training" section</li>
                            <li>Select which training types should be free for new members:</li>
                            <ul>
                                <li>Check the boxes for training types you want to offer for free</li>
                                <li>Examples: WOD, Weightlifting, Cardio, etc.</li>
                            </ul>
                            <li>Save your settings</li>
                        </ol>
                        <Typography paragraph>
                            How it works:
                        </Typography>
                        <ul>
                            <li>When a new member registers for a class of the selected type for the first time, they won't be charged a session</li>
                            <li>This applies only to their first registration for each training type</li>
                            <li>The member doesn't need a plan to use this feature</li>
                            <li>After the first free class, normal plan rules apply</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Offering free first classes is an excellent way to let potential members try your gym before committing to a membership. It reduces the barrier to entry and increases conversions.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'warning.main', mt: 1 }}>
                            Make sure your members understand this is a one-time offer per training type to avoid confusion.
                        </Typography>
                    </>
                ),
                tags: ['first free', 'trial', 'new members', 'promotions', 'settings'],
                roles: ['affiliate']
            },
            {
                title: 'Cancellation Policy Settings',
                content: (
                    <>
                        <Typography paragraph>
                            Set up cancellation policies for class registrations:
                        </Typography>
                        <ol>
                            <li>Navigate to "My Affiliate"</li>
                            <li>Click on the "My Affiliate" tab</li>
                            <li>Find the "Cancellation Policy" section</li>
                            <li>Set "Cancel Before Hours":</li>
                            <ul>
                                <li>Enter the number of hours before class start when cancellation is allowed</li>
                                <li>Example: Setting it to 2 means members must cancel at least 2 hours before class</li>
                            </ul>
                            <li>Save your settings</li>
                        </ol>
                        <Typography paragraph>
                            How the cancellation policy works:
                        </Typography>
                        <ul>
                            <li>If a member cancels before the deadline, their session is returned to their plan</li>
                            <li>If they cancel after the deadline, the session is lost</li>
                            <li>Late cancellations prevent last-minute dropouts and help manage class capacity</li>
                            <li>The system automatically enforces this policy</li>
                        </ul>
                        <Typography paragraph>
                            Best practices:
                        </Typography>
                        <ul>
                            <li>Most gyms set cancellation windows between 1-4 hours</li>
                            <li>Clearly communicate your policy to members</li>
                            <li>Consider your class capacity when setting the window</li>
                            <li>Be consistent in enforcing the policy</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            A clear cancellation policy helps manage class capacity effectively and reduces no-shows, ensuring better experiences for all members.
                        </Typography>
                    </>
                ),
                tags: ['cancellation', 'policy', 'rules', 'registration', 'settings'],
                roles: ['affiliate']
            },
        ],
        smartLocks: [
            {
                title: 'Smart Lock Overview',
                content: (
                    <>
                        <Typography paragraph>
                            Smart Lock lets an affiliate connect door locks through Seam and control who can open doors from Irontrack.
                        </Typography>
                        <Typography paragraph>
                            The smart lock module has four main areas:
                        </Typography>
                        <ul>
                            <li><strong>Setup:</strong> Enable smart locks, save the Seam API key, load Seam devices, and create Irontrack doors.</li>
                            <li><strong>Policies:</strong> Define access rules such as membership, class booking, plans, roles, time windows, and limits.</li>
                            <li><strong>Overrides & Blocks:</strong> Give temporary manual access or block a user from opening doors.</li>
                            <li><strong>Access Logs:</strong> Review granted and denied unlock attempts with reasons.</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Irontrack decides whether a user may open the door. The physical lock still handles hardware behavior such as auto-lock if the lock supports it.
                        </Typography>
                    </>
                ),
                tags: ['smart lock', 'nutilukk', 'door access', 'seam', 'overview', 'unlock'],
                roles: ['affiliate']
            },
            {
                title: 'Seam Setup and API Key',
                content: (
                    <>
                        <Typography paragraph>
                            Irontrack uses Seam as the smart lock provider. Each club should use its own Seam workspace/API key so clubs stay separated.
                        </Typography>
                        <ol>
                            <li>Create or open the club's Seam workspace.</li>
                            <li>Connect the lock account or virtual sandbox lock in Seam.</li>
                            <li>Copy the Seam API key from Seam.</li>
                            <li>In Irontrack, go to <strong>My Affiliate → Smart Lock → Setup</strong>.</li>
                            <li>Paste the Seam API key and click <strong>Save Key</strong>.</li>
                            <li>Click <strong>Load Seam Devices</strong> to fetch locks from Seam.</li>
                        </ol>
                        <Typography paragraph>
                            <strong>Workspace ID</strong> and <strong>Connected Account ID</strong> are optional metadata. Unlocking and device listing normally only require the Seam API key.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'warning.main', mt: 1 }}>
                            The API key is encrypted in the backend before it is stored. Do not share keys in email, chat, or screenshots.
                        </Typography>
                    </>
                ),
                tags: ['seam', 'api key', 'workspace', 'connected account', 'setup', 'encrypted'],
                roles: ['affiliate']
            },
            {
                title: 'Creating Doors from Seam Devices',
                content: (
                    <>
                        <Typography paragraph>
                            A door in Irontrack is linked to a Seam device. Policies are assigned to Irontrack doors.
                        </Typography>
                        <ol>
                            <li>Open <strong>Smart Lock → Setup</strong>.</li>
                            <li>Click <strong>Load Seam Devices</strong>.</li>
                            <li>Select the Seam device.</li>
                            <li>Enter a clear door name, for example <strong>Main Entrance</strong> or <strong>Back Door</strong>.</li>
                            <li>Optionally add a location label, for example <strong>Ground floor</strong>.</li>
                            <li>Click <strong>Save Door</strong>.</li>
                        </ol>
                        <Typography paragraph>
                            Example setup:
                        </Typography>
                        <ul>
                            <li><strong>Main Entrance:</strong> visible for regular member class access.</li>
                            <li><strong>Staff Door:</strong> linked only to trainer or affiliate policies.</li>
                            <li><strong>24/7 Door:</strong> linked to contract client or special access policies.</li>
                        </ul>
                    </>
                ),
                tags: ['doors', 'seam devices', 'main entrance', 'device', 'setup'],
                roles: ['affiliate']
            },
            {
                title: 'Policy Basics',
                content: (
                    <>
                        <Typography paragraph>
                            A policy is a set of rules that can grant access to one or more doors. If a door has multiple active policies, access is allowed when at least one policy matches.
                        </Typography>
                        <Typography paragraph>
                            The policy editor is split into blocks:
                        </Typography>
                        <ul>
                            <li><strong>Basic Rules:</strong> policy name, active status, validity dates, membership/class booking requirements, and roles.</li>
                            <li><strong>Access Scope:</strong> selected users, plans, training types, and doors.</li>
                            <li><strong>Weekly Schedule:</strong> days and regular time windows.</li>
                            <li><strong>Limits:</strong> daily limits, cooldown, and rolling-window limits.</li>
                            <li><strong>Special Dates:</strong> holidays, closed days, and one-off custom schedules.</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Tip: Give every policy a descriptive name, such as "Members before class", "Trainers all day", or "Contract clients 24/7".
                        </Typography>
                    </>
                ),
                tags: ['policy', 'rules', 'conditions', 'access scope', 'basic rules'],
                roles: ['affiliate']
            },
            {
                title: 'Basic Rules Explained',
                content: (
                    <>
                        <Typography paragraph>
                            Use Basic Rules to decide when a policy is active and what core requirements must be true.
                        </Typography>
                        <ul>
                            <li><strong>Policy active:</strong> If disabled, the policy cannot grant access.</li>
                            <li><strong>Valid From / Valid Until:</strong> Optional date range for temporary policies.</li>
                            <li><strong>Require active membership:</strong> User must be an active member of the affiliate.</li>
                            <li><strong>Require class booking:</strong> User must be booked into the selected class.</li>
                            <li><strong>Minutes before class entry is allowed:</strong> Example: 15 means the user can open the door starting 15 minutes before class.</li>
                            <li><strong>Auto check-in on unlock:</strong> When unlocking for a class, the user is marked as checked in after successful unlock.</li>
                            <li><strong>Allow contract clients:</strong> Allows users with valid contract-based plans. If no allowed plans are selected, only contract clients can satisfy the entitlement part.</li>
                            <li><strong>Allowed roles:</strong> Choose whether affiliate, trainer, and/or regular users can match the policy.</li>
                        </ul>
                        <Typography paragraph>
                            Example: A class access policy could require active membership, require class booking, allow entry 15 minutes before class, and auto check-in on unlock.
                        </Typography>
                    </>
                ),
                tags: ['basic rules', 'valid from', 'valid until', 'class booking', 'auto check-in', 'contract clients', 'roles'],
                roles: ['affiliate']
            },
            {
                title: 'Access Scope: Users, Plans, Training Types, and Doors',
                content: (
                    <>
                        <Typography paragraph>
                            Access Scope controls exactly who and what the policy applies to.
                        </Typography>
                        <ul>
                            <li><strong>Assign policy to specific users:</strong> If selected, only those users can match the policy.</li>
                            <li><strong>Allowed plans:</strong> If selected, the user must have one of those plans and sessions left must be greater than 0.</li>
                            <li><strong>Allowed Training Types:</strong> Restrict the policy to specific class types, such as WOD, Open Gym, or Weightlifting.</li>
                            <li><strong>Policy Doors:</strong> Select which doors this policy can open.</li>
                        </ul>
                        <Typography paragraph>
                            Examples:
                        </Typography>
                        <ul>
                            <li><strong>Open Gym only:</strong> Select the Open Gym training type and require class booking.</li>
                            <li><strong>Premium plan door:</strong> Select only the premium plan in Allowed plans.</li>
                            <li><strong>Staff-only door:</strong> Link the policy only to the staff door and allow trainer/affiliate roles.</li>
                        </ul>
                    </>
                ),
                tags: ['users', 'plans', 'training types', 'doors', 'scope', 'sessions left'],
                roles: ['affiliate']
            },
            {
                title: 'Weekly Schedule and Daily Time Windows',
                content: (
                    <>
                        <Typography paragraph>
                            Weekly Schedule defines when the policy can grant access during normal weeks.
                        </Typography>
                        <ul>
                            <li><strong>Simple schedule:</strong> Select weekdays and use one shared start/end time.</li>
                            <li><strong>Separate daily time windows:</strong> Enable this if each day needs a different time range.</li>
                            <li><strong>Overnight windows:</strong> A window like 22:00-05:00 is treated as overnight.</li>
                        </ul>
                        <Typography paragraph>
                            Example schedules:
                        </Typography>
                        <ul>
                            <li><strong>Business hours:</strong> Monday-Friday, 06:00-22:00.</li>
                            <li><strong>Weekend reduced hours:</strong> Monday-Friday 06:00-22:00, Saturday-Sunday 09:00-18:00 using daily windows.</li>
                            <li><strong>Night access:</strong> 22:00-05:00 for selected users or contract clients.</li>
                        </ul>
                    </>
                ),
                tags: ['weekly schedule', 'daily time windows', 'weekdays', 'time window', 'overnight'],
                roles: ['affiliate']
            },
            {
                title: 'Limits: Daily, Cooldown, and Rolling Window',
                content: (
                    <>
                        <Typography paragraph>
                            Limits prevent repeated unlock attempts or door-sharing behavior.
                        </Typography>
                        <ul>
                            <li><strong>Max unlocks per day:</strong> Maximum successful unlocks per user per day.</li>
                            <li><strong>Cooldown seconds:</strong> Minimum wait before the same user can open the same door again.</li>
                            <li><strong>Max unlocks per rolling window:</strong> Maximum unlocks within the last X minutes.</li>
                            <li><strong>Rolling window minutes:</strong> The moving time range used for counting unlocks.</li>
                        </ul>
                        <Typography paragraph>
                            Rolling window example:
                        </Typography>
                        <ul>
                            <li>Max unlocks per rolling window: <strong>3</strong></li>
                            <li>Rolling window minutes: <strong>60</strong></li>
                            <li>At 13:20, Irontrack checks unlocks from 12:20 to 13:20.</li>
                            <li>At 13:45, Irontrack checks unlocks from 12:45 to 13:45.</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            A practical setup is cooldown 60 seconds and max 3 unlocks in the last 60 minutes.
                        </Typography>
                    </>
                ),
                tags: ['limits', 'cooldown', 'rolling window', 'daily limit', 'spam', 'unlock limit'],
                roles: ['affiliate']
            },
            {
                title: 'Special Dates, Holidays, and Closed Days',
                content: (
                    <>
                        <Typography paragraph>
                            Special Dates override the normal weekly schedule for a specific date.
                        </Typography>
                        <ul>
                            <li><strong>Closed:</strong> Door access is denied for that policy on the selected date.</li>
                            <li><strong>Custom time window:</strong> Define a different start/end time just for that date.</li>
                            <li><strong>Note:</strong> Add a reason, such as "Christmas", "Maintenance", or "Competition day".</li>
                        </ul>
                        <Typography paragraph>
                            Examples:
                        </Typography>
                        <ul>
                            <li><strong>24.12 closed:</strong> Set date 24.12 and mark Closed.</li>
                            <li><strong>Competition day:</strong> Set a custom access window 08:00-20:00.</li>
                            <li><strong>Maintenance:</strong> Close access for one day while work is happening.</li>
                        </ul>
                    </>
                ),
                tags: ['special dates', 'holiday', 'closed day', 'maintenance', 'custom schedule'],
                roles: ['affiliate']
            },
            {
                title: 'Manual Access Overrides',
                content: (
                    <>
                        <Typography paragraph>
                            Manual Access is for exceptions. It lets an affiliate give a user temporary access even when a normal policy would not match.
                        </Typography>
                        <ol>
                            <li>Open <strong>Smart Lock → Overrides & Blocks</strong>.</li>
                            <li>Select the user.</li>
                            <li>Select a specific door or choose all doors.</li>
                            <li>Set optional Valid From and Valid Until dates.</li>
                            <li>Add a reason, such as "trial class" or "temporary trainer".</li>
                            <li>Click <strong>Save Manual Access</strong>.</li>
                        </ol>
                        <Typography paragraph>
                            Examples:
                        </Typography>
                        <ul>
                            <li>Give a trial user access only today from 17:30-19:00.</li>
                            <li>Give a guest coach access for one week.</li>
                            <li>Allow a member with a payment issue to enter once while the issue is resolved.</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'warning.main', mt: 1 }}>
                            Blocked users are still denied even if they have manual access.
                        </Typography>
                    </>
                ),
                tags: ['manual access', 'override', 'temporary access', 'trial', 'guest coach'],
                roles: ['affiliate']
            },
            {
                title: 'Blocked Users',
                content: (
                    <>
                        <Typography paragraph>
                            Blocking a user prevents them from opening smart lock doors before any manual access or policy is checked.
                        </Typography>
                        <ol>
                            <li>Open <strong>Smart Lock → Overrides & Blocks</strong>.</li>
                            <li>Select the user.</li>
                            <li>Optionally set Valid From and Valid Until.</li>
                            <li>Add a block reason.</li>
                            <li>Click <strong>Block User</strong>.</li>
                        </ol>
                        <Typography paragraph>
                            Use blocked users for security issues, unpaid membership disputes, temporary bans, or any case where the user must not open the door.
                        </Typography>
                    </>
                ),
                tags: ['blocked user', 'block', 'ban', 'security', 'deny access'],
                roles: ['affiliate']
            },
            {
                title: 'Access Logs and Audit Trail',
                content: (
                    <>
                        <Typography paragraph>
                            Access Logs show what happened when users attempted to open doors.
                        </Typography>
                        <ul>
                            <li><strong>Time:</strong> Shown in European date format.</li>
                            <li><strong>User:</strong> Who attempted access.</li>
                            <li><strong>Door:</strong> Which door was requested.</li>
                            <li><strong>Policy:</strong> Which policy granted access, if any.</li>
                            <li><strong>Status:</strong> Granted or denied.</li>
                            <li><strong>Reason:</strong> Why the request was granted or denied.</li>
                        </ul>
                        <Typography paragraph>
                            Logs are loaded 50 at a time. Use Next and Previous to move through older events.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Logs are useful for support, security review, and explaining to members why access was denied.
                        </Typography>
                    </>
                ),
                tags: ['logs', 'audit', 'events', 'granted', 'denied', 'reason', 'pagination'],
                roles: ['affiliate']
            },
            {
                title: 'Opening Doors as a User',
                content: (
                    <>
                        <Typography paragraph>
                            Users open doors from class or schedule views where the Open Door button is shown.
                        </Typography>
                        <ul>
                            <li>Affiliate and trainer roles can see Open Door on the Classes page near class controls.</li>
                            <li>Regular users see Open Door in the class modal below class details when they are eligible.</li>
                            <li>For regular users, Irontrack only shows doors that the user is currently allowed to open.</li>
                            <li>If access is denied, the user sees the reason returned by the policy check.</li>
                        </ul>
                        <Typography paragraph>
                            Common denied reasons include no active plan with sessions left, being too early before class, current time outside the access window, or the user being blocked.
                        </Typography>
                    </>
                ),
                tags: ['open door', 'class modal', 'classes page', 'regular user', 'trainer', 'affiliate', 'eligible doors'],
                roles: ['affiliate']
            },
            {
                title: 'Example Smart Lock Policy Setups',
                content: (
                    <>
                        <Typography paragraph>
                            Use these examples as starting points.
                        </Typography>
                        <Typography variant="subtitle2">Example 1: Regular member class access</Typography>
                        <ul>
                            <li>Require active membership: enabled.</li>
                            <li>Require class booking: enabled.</li>
                            <li>Minutes before class: 15.</li>
                            <li>Auto check-in: enabled.</li>
                            <li>Allowed roles: Regular.</li>
                            <li>Doors: Main Entrance.</li>
                        </ul>
                        <Typography variant="subtitle2">Example 2: Trainer access</Typography>
                        <ul>
                            <li>Allowed roles: Trainer and Affiliate.</li>
                            <li>Require class booking: disabled.</li>
                            <li>Weekly schedule: Monday-Sunday, 05:00-23:00.</li>
                            <li>Doors: Main Entrance and Staff Door.</li>
                        </ul>
                        <Typography variant="subtitle2">Example 3: Contract clients 24/7</Typography>
                        <ul>
                            <li>Allow contract clients: enabled.</li>
                            <li>Allowed plans: leave empty if only contract clients should match.</li>
                            <li>Weekly schedule: all days, 00:00-23:59.</li>
                            <li>Limits: cooldown 60 seconds, rolling window 3 unlocks / 60 minutes.</li>
                        </ul>
                        <Typography variant="subtitle2">Example 4: Open Gym plan only</Typography>
                        <ul>
                            <li>Allowed plans: select Open Gym plan.</li>
                            <li>Allowed Training Types: Open Gym.</li>
                            <li>Require class booking: enabled if Open Gym uses class registrations.</li>
                            <li>Doors: Main Entrance.</li>
                        </ul>
                    </>
                ),
                tags: ['examples', 'policy examples', 'class access', 'trainer access', 'contract clients', 'open gym'],
                roles: ['affiliate']
            },
            {
                title: 'Auto-lock and Lock App Settings',
                content: (
                    <>
                        <Typography paragraph>
                            Irontrack sends an unlock command through Seam. Whether the door locks again automatically depends on the physical lock and its vendor app.
                        </Typography>
                        <ul>
                            <li><strong>Auto-lock:</strong> Usually configured in the lock manufacturer's app.</li>
                            <li><strong>Passage / office mode:</strong> Some locks support staying unlocked during certain hours, but not all locks support it.</li>
                            <li><strong>Seam:</strong> Can expose supported lock capabilities, but cannot add features the lock itself does not support.</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Recommended setup: configure auto-lock in the lock's own app and use Irontrack only for deciding who may unlock the door.
                        </Typography>
                    </>
                ),
                tags: ['auto lock', 'autolock', 'passage mode', 'office mode', 'lock app', 'vendor'],
                roles: ['affiliate']
            },
        ],
        messaging: [
            {
                title: 'Sending Messages',
                content: (
                    <>
                        <Typography paragraph>
                            To send messages to members or groups:
                        </Typography>
                        <ol>
                            <li>Navigate to the "Messages" section</li>
                            <li>Click on the "Send Message" tab</li>
                            <li>Choose recipient type:</li>
                            <ul>
                                <li>User: Send to an individual member</li>
                                <li>Group: Send to a predefined group of members</li>
                                <li>All Members: Send to everyone in your affiliate</li>
                            </ul>
                            <li>Search for and select the recipient</li>
                            <li>Enter a subject and message body</li>
                            <li>Click "Send" to deliver your message</li>
                        </ol>
                        <Typography paragraph>
                            Messages are delivered to users' email addresses and also appear in their Messages section.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            You can also quickly send messages to all attendees of a class by going to the class details and clicking the "Send Message" button.
                        </Typography>
                    </>
                ),
                tags: ['messages', 'communication', 'email', 'inbox', 'notifications'],
                roles: ['affiliate', 'trainer']
            },
            {
                title: 'Managing Contact Groups',
                content: (
                    <>
                        <Typography paragraph>
                            Create and manage contact groups:
                        </Typography>
                        <ol>
                            <li>Go to the "Messages" section</li>
                            <li>Select the "Groups" tab</li>
                            <li>Click "Add Group" to create a new group</li>
                            <li>Enter a group name</li>
                            <li>Search for and add members to the group</li>
                            <li>Save the group</li>
                        </ol>
                        <Typography paragraph>
                            Groups help you send messages to specific sets of members more efficiently. For example, you could create groups for:
                        </Typography>
                        <ul>
                            <li>Morning class attendees</li>
                            <li>Weightlifting enthusiasts</li>
                            <li>Competitors</li>
                            <li>New members</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Organizing members into logical groups saves time and ensures your communications reach the right audience.
                        </Typography>
                    </>
                ),
                tags: ['groups', 'contacts', 'distribution lists', 'batch messaging'],
                roles: ['affiliate']
            },
            {
                title: 'Viewing Message History',
                content: (
                    <>
                        <Typography paragraph>
                            To view your sent messages:
                        </Typography>
                        <ol>
                            <li>Navigate to the "Messages" section</li>
                            <li>Select the "Sent Messages" tab</li>
                            <li>View the list of all messages you've sent</li>
                            <li>Click on a message to expand and see its full content</li>
                            <li>Messages are sorted by date with the most recent at the top</li>
                        </ol>
                        <Typography paragraph>
                            This history helps you track your communication with members and groups. You can see when messages were sent and to whom.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            The message history can be helpful for verifying if important announcements were sent and ensuring consistent communication.
                        </Typography>
                    </>
                ),
                tags: ['message history', 'sent items', 'communications log'],
                roles: ['affiliate', 'trainer']
            },
            {
                title: 'Automated Notifications',
                content: (
                    <>
                        <Typography paragraph>
                            Irontrack sends automatic notifications to users for several events:
                        </Typography>
                        <ul>
                            <li><strong>Contract Notifications:</strong> When a new contract is sent for signature</li>
                            <li><strong>Plan Expiration:</strong> When a membership plan is about to expire</li>
                            <li><strong>Class Reminders:</strong> Before scheduled classes</li>
                            <li><strong>Payment Confirmations:</strong> After successful payments</li>
                            <li><strong>Waitlist Updates:</strong> When a spot becomes available in a class</li>
                        </ul>
                        <Typography paragraph>
                            These notifications are sent automatically by the system to improve user experience and ensure important information is communicated promptly.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Automated notifications help reduce administrative work while keeping members informed about important updates.
                        </Typography>
                    </>
                ),
                tags: ['notifications', 'automatic', 'reminders', 'emails', 'alerts'],
                roles: ['affiliate', 'trainer']
            },
            {
                title: 'Communication Best Practices',
                content: (
                    <>
                        <Typography paragraph>
                            Tips for effective communication with your members:
                        </Typography>
                        <ul>
                            <li><strong>Be Concise:</strong> Keep messages clear and to the point</li>
                            <li><strong>Use Descriptive Subjects:</strong> Help members quickly understand the message purpose</li>
                            <li><strong>Target Your Audience:</strong> Use groups to send relevant messages to specific members</li>
                            <li><strong>Maintain Consistency:</strong> Establish a regular communication schedule for updates</li>
                            <li><strong>Include Call to Action:</strong> Clearly state what you want members to do in response</li>
                        </ul>
                        <Typography paragraph>
                            Regular communication helps keep members engaged and informed about your gym's activities, events, and important announcements.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Consider creating a communication plan with regular updates, such as weekly programming previews or monthly newsletters.
                        </Typography>
                    </>
                ),
                tags: ['communication', 'best practices', 'effective messaging', 'engagement'],
                roles: ['affiliate', 'trainer']
            },
        ],
        troubleshooting: [
            {
                title: 'Common Login Issues',
                content: (
                    <>
                        <Typography paragraph>
                            If you're having trouble logging in:
                        </Typography>
                        <ol>
                            <li>Make sure you're using the correct email address</li>
                            <li>Check that Caps Lock is not enabled when entering your password</li>
                            <li>Try resetting your password using the "Forgot Password" link</li>
                            <li>Clear your browser cache and cookies</li>
                            <li>Try a different browser</li>
                        </ol>
                        <Typography paragraph>
                            If you've recently registered, verify that you've confirmed your email by checking your inbox and clicking the verification link.
                        </Typography>
                        <Typography paragraph>
                            If you still can't log in, contact your affiliate owner or our support team.
                        </Typography>
                    </>
                ),
                tags: ['login', 'password', 'access', 'authentication', 'email verification'],
                roles: ['all']
            },
            {
                title: 'Payment Problems',
                content: (
                    <>
                        <Typography paragraph>
                            If you encounter payment issues:
                        </Typography>
                        <ol>
                            <li>Verify that you have sufficient funds</li>
                            <li>Check if your bank is blocking the transaction</li>

                        </ol>
                        <Typography paragraph>
                            For regular members: Contact your gym owner for assistance.
                        </Typography>
                        <Typography paragraph>
                            For affiliate owners: Check transaction logs in the Finance section and contact our support if issues persist. If using Montonio for payments, also check your Montonio dashboard.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Using the credit system can be a good workaround for temporary payment issues. Affiliate owners can add credit to a member's account, which they can then use for purchases.
                        </Typography>
                    </>
                ),
                tags: ['payment', 'transaction', 'credit card', 'bank', 'purchase'],
                roles: ['all']
            },
            {
                title: 'Class Registration Issues',
                content: (
                    <>
                        <Typography paragraph>
                            If you can't register for a class:
                        </Typography>
                        <ol>
                            <li>Check if you have an active plan with available sessions</li>
                            <li>Verify that the class isn't already full (at capacity)</li>
                            <li>Ensure your plan covers the type of training for that class</li>
                            <li>Check if you're already registered for another class at the same time</li>
                            <li>Refresh the page and try again</li>
                        </ol>
                        <Typography paragraph>
                            If you have an unlimited plan but still can't register, check the plan's expiration date. For other issues, contact your affiliate owner for assistance.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Remember that each plan is linked to specific training types. For example, if your plan only covers "WOD" classes, you won't be able to register for "Weightlifting" classes.
                        </Typography>
                    </>
                ),
                tags: ['class registration', 'booking', 'sessions', 'plans'],
                roles: ['regular']
            },
            {
                title: 'Contract Issues',
                content: (
                    <>
                        <Typography paragraph>
                            For affiliate owners with contract issues:
                        </Typography>
                        <ol>
                            <li>If you can't send a contract, verify that it's in "draft" status</li>
                            <li>Ensure all required fields are completed in the contract</li>
                            <li>Check that the user email is correct</li>
                            <li>For contracts stuck in "Waiting for acceptance", you can delete and recreate them</li>
                            <li>If a contract is accepted but payments aren't processing, check your Montonio settings</li>
                            <li>If members cannot see a base contract on Buy Plans, confirm it is Active on the Plans page</li>
                            <li>If a member cannot join again, check whether they already have an accepted or unpaid contract from the same base contract</li>
                            <li>Unpaid self-service joins that never reach checkout are removed automatically after 48 hours</li>
                            <li>If a contract request email does not arrive, check spam and that the club email is set on the affiliate</li>
                            <li>Contract requests are not stored in Irontrack — if you delete the email, the answers are gone. Create the contract from the email while you still have it</li>
                        </ol>
                        <Typography paragraph>
                            For contract template issues, try creating a new template from scratch rather than editing an existing one. This can resolve formatting issues.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Keep contract terms clear and concise to increase the likelihood of quick acceptance by members. See "Contracts" for the full setup of contracts, base contracts, and contract requests.
                        </Typography>
                    </>
                ),
                tags: ['contracts', 'drafts', 'templates', 'acceptance', 'payments', 'base contract', 'contract request'],
                roles: ['affiliate']
            },
            {
                title: 'Technical Support',
                content: (
                    <>
                        <Typography paragraph>
                            For technical issues with the Irontrack platform:
                        </Typography>
                        <ol>
                            <li>Check this help section for guidance on your specific issue</li>
                            <li>Try refreshing the page or logging out and back in</li>
                            <li>Clear your browser cache and cookies</li>
                            <li>Contact our support team at <Link href="mailto:info@irontrack.ee">info@irontrack.ee</Link></li>
                        </ol>
                        <Typography paragraph>
                            When contacting support, please include:
                        </Typography>
                        <ul>
                            <li>Your username and email</li>
                            <li>Description of the issue</li>
                            <li>Steps to reproduce the problem</li>
                            <li>Screenshots if possible</li>
                            <li>Browser and device information</li>
                        </ul>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Most technical issues can be resolved by clearing your browser cache and cookies, or by trying a different browser.
                        </Typography>
                    </>
                ),
                tags: ['support', 'help', 'technical issues', 'bugs', 'contact'],
                roles: ['all']
            },
            {
                title: 'Handling Session Count Issues',
                content: (
                    <>
                        <Typography paragraph>
                            If session counts for a member's plan are incorrect:
                        </Typography>
                        <ol>
                            <li>As an affiliate owner or trainer, go to the member's profile</li>
                            <li>Click on the "Active Plans" tab</li>
                            <li>Find the plan with the incorrect session count</li>
                            <li>Click the "Edit" button for that plan</li>
                            <li>Adjust the "Sessions Left" value to the correct number</li>
                            <li>Click "Save" to update the plan</li>
                        </ol>
                        <Typography paragraph>
                            This is useful when needing to correct errors or provide compensation sessions. The system logs all changes for audit purposes.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Consider adding a note explaining why the session count was adjusted to maintain transparency.
                        </Typography>
                    </>
                ),
                tags: ['sessions', 'count', 'plans', 'adjustment', 'correction'],
                roles: ['affiliate', 'trainer']
            },
            {
                title: 'Mobile App Troubleshooting',
                content: (
                    <>
                        <Typography paragraph>
                            If you're experiencing issues with the mobile version:
                        </Typography>
                        <ol>
                            <li>Ensure you're using the latest version of your mobile browser</li>
                            <li>Check your internet connection</li>
                            <li>Clear your browser cache and cookies</li>
                            <li>Try switching between WiFi and mobile data</li>
                            <li>If using iOS, try disabling content blockers</li>
                        </ol>
                        <Typography paragraph>
                            For affiliate owners and trainers: Some advanced features may work better on desktop or tablet devices due to screen size limitations.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            The responsive design should work well on most mobile devices, but for complex operations like analytics or extensive member management, a larger screen may provide a better experience.
                        </Typography>
                    </>
                ),
                tags: ['mobile', 'app', 'smartphone', 'tablet', 'responsive'],
                roles: ['all']
            },
            {
                title: 'Data Export Issues',
                content: (
                    <>
                        <Typography paragraph>
                            If you're having trouble exporting data:
                        </Typography>
                        <ol>
                            <li>Check that you have the necessary permissions for the data you're trying to export</li>
                            <li>Try exporting smaller date ranges if dealing with large amounts of data</li>
                            <li>Ensure you have a stable internet connection</li>
                            <li>If using filters, try simplifying your filter criteria</li>
                            <li>Try using a different browser</li>
                        </ol>
                        <Typography paragraph>
                            For large exports, the process may take some time. Be patient and avoid refreshing the page during export.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mt: 1 }}>
                            Regular data exports are recommended for backup purposes, especially for important financial and membership information.
                        </Typography>
                    </>
                ),
                tags: ['export', 'data', 'download', 'backup', 'reports'],
                roles: ['affiliate']
            },
        ]
    };

    // All content flattened for search
    const allHelpItems = [
        ...helpContent.gettingStarted,
        ...helpContent.regularUsers,
        ...helpContent.trainers,
        ...helpContent.affiliateOwners,
        ...(isAffiliateOwner ? helpContent.smartLocks : []),
        ...helpContent.messaging,
        ...helpContent.troubleshooting
    ];

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Handle search
    const handleSearchChange = (event) => {
        const query = event.target.value.toLowerCase();
        setSearchQuery(query);

        if (query === '') {
            setFilteredHelp([]);
            return;
        }

        // Filter help items based on search query
        const filtered = allHelpItems.filter(item => {
            // Check if the search terms match the title or content
            const titleMatch = item.title.toLowerCase().includes(query);
            const contentMatch = typeof item.content === 'string'
                ? item.content.toLowerCase().includes(query)
                : false;
            const tagsMatch = item.tags.some(tag => tag.toLowerCase().includes(query));

            return titleMatch || contentMatch || tagsMatch;
        });

        setFilteredHelp(filtered);
    };

    // Filter content by role
    const getVisibleContent = (contentArray) => {
        if (!isLoggedIn) return contentArray.filter(item => item.roles.includes('all'));

        return contentArray.filter(item => {
            return item.roles.includes('all') ||
                item.roles.includes(role) ||
                (role === 'affiliate' && item.roles.includes('trainer')); // Affiliate owners can see trainer content
        });
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <IOSInstallModal open={iOSModalOpen} handleClose={() => setIOSModalOpen(false)} />
            <AndroidInstallModal open={androidModalOpen} handleClose={() => setAndroidModalOpen(false)} />

            <SEO
                title="Help & Documentation - Irontrack"
                description="Get help with using the Irontrack gym management platform. Find answers to common questions and learn how to use all features."
                keywords="help, documentation, support, FAQ, Irontrack, gym management"
            />

            <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                    <HelpOutlineIcon sx={{ mr: 1, fontSize: 32 }} />
                    Help Center
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" paragraph>
                    Welcome to the Irontrack Help Center. Find answers to common questions and learn how to use all features of our platform.
                </Typography>

                <TextField
                    fullWidth
                    placeholder="Search for help..."
                    variant="outlined"
                    sx={{ mb: 3, mt: 2 }}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Paper>

            {searchQuery ? (
                // Search results
                <Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                        Search Results for "{searchQuery}"
                    </Typography>

                    {filteredHelp.length === 0 ? (
                        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'background.paper' }}>
                            <Typography variant="subtitle1">
                                No results found for "{searchQuery}". Try different keywords or browse the help categories below.
                            </Typography>

                            <Button
                                variant="contained"
                                color="primary"
                                sx={{ mt: 2 }}
                                onClick={() => setSearchQuery('')}
                            >
                                Clear Search
                            </Button>
                        </Paper>
                    ) : (
                        filteredHelp.map((item, index) => (
                            <Accordion key={index} sx={{ mb: 2 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        {item.title}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ mb: 2 }}>
                                        {item.content}
                                    </Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {item.tags.map((tag, tagIndex) => (
                                            <Chip key={tagIndex} label={tag} size="small" />
                                        ))}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        ))
                    )}
                </Box>
            ) : (
                // Regular help content
                <Box>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        variant={isMobile ? "scrollable" : "fullWidth"}
                        scrollButtons="auto"
                        sx={{
                            mb: 3,
                            '& .MuiTab-root': {
                                minHeight: '72px',
                                display: 'flex',
                                flexDirection: 'column'
                            }
                        }}
                    >
                        <Tab
                            value={0}
                            icon={<SchoolIcon />}
                            iconPosition="top"
                            label="Getting Started"
                        />
                        <Tab
                            value={1}
                            icon={<AccountCircleIcon />}
                            iconPosition="top"
                            label="For Members"
                        />
                        <Tab
                            value={2}
                            icon={<FitnessCenterIcon />}
                            iconPosition="top"
                            label="For Trainers"
                        />
                        <Tab
                            value={3}
                            icon={<BusinessIcon />}
                            iconPosition="top"
                            label="For Affiliate Owners"
                        />
                        {isAffiliateOwner && (
                            <Tab
                                value={4}
                                icon={<LockOpenIcon />}
                                iconPosition="top"
                                label="Smart Locks"
                            />
                        )}
                        <Tab
                            value={5}
                            icon={<MessageIcon />}
                            iconPosition="top"
                            label="Messaging"
                        />
                        <Tab
                            value={6}
                            icon={<SettingsIcon />}
                            iconPosition="top"
                            label="Troubleshooting"
                        />
                    </Tabs>

                    <Box>
                        {/* Getting Started */}
                        {tabValue === 0 && (
                            <Box>
                                <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
                                    Getting Started with Irontrack
                                </Typography>

                                {getVisibleContent(helpContent.gettingStarted).map((item, index) => (
                                    <Accordion key={index} sx={{ mb: 2 }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {item.title}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {item.content}
                                        </AccordionDetails>
                                    </Accordion>
                                ))}


                            </Box>
                        )}

                        {/* For Members */}
                        {tabValue === 1 && (
                            <Box>
                                <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
                                    For Members
                                </Typography>

                                {getVisibleContent(helpContent.regularUsers).map((item, index) => (
                                    <Accordion key={index} sx={{ mb: 2 }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {item.title}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {item.content}
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        )}

                        {/* For Trainers */}
                        {tabValue === 2 && (
                            <Box>
                                <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
                                    For Trainers
                                </Typography>

                                {getVisibleContent(helpContent.trainers).map((item, index) => (
                                    <Accordion key={index} sx={{ mb: 2 }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {item.title}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {item.content}
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        )}

                        {/* For Affiliate Owners */}
                        {tabValue === 3 && (
                            <Box>
                                <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
                                    For Affiliate Owners
                                </Typography>

                                {getVisibleContent(helpContent.affiliateOwners).map((item, index) => (
                                    <Accordion key={index} sx={{ mb: 2 }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {item.title}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {item.content}
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        )}

                        {/* Smart Locks */}
                        {isAffiliateOwner && tabValue === 4 && (
                            <Box>
                                <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
                                    Smart Locks
                                </Typography>

                                {getVisibleContent(helpContent.smartLocks).map((item, index) => (
                                    <Accordion key={index} sx={{ mb: 2 }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {item.title}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {item.content}
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        )}

                        {/* Messaging */}
                        {tabValue === 5 && (
                            <Box>
                                <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
                                    Messaging System
                                </Typography>

                                {getVisibleContent(helpContent.messaging).map((item, index) => (
                                    <Accordion key={index} sx={{ mb: 2 }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {item.title}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {item.content}
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        )}

                        {/* Troubleshooting */}
                        {tabValue === 6 && (
                            <Box>
                                <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
                                    Troubleshooting
                                </Typography>

                                {getVisibleContent(helpContent.troubleshooting).map((item, index) => (
                                    <Accordion key={index} sx={{ mb: 2 }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {item.title}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {item.content}
                                        </AccordionDetails>
                                    </Accordion>
                                ))}

                                <Box sx={{ mt: 4, p: 3, bgcolor: 'primary.50', borderRadius: 2, textAlign: 'center' }}>
                                    <Typography variant="h6" gutterBottom>
                                        Still need help?
                                    </Typography>
                                    <Typography paragraph sx={{ mb: 3 }}>
                                        Contact our support team for personalized assistance.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        href="mailto:info@irontrack.ee"
                                        startIcon={<HelpOutlineIcon />}
                                    >
                                        Contact Support
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            {/* Quick Links Footer */}
            <Paper
                elevation={2}
                sx={{
                    mt: 6,
                    p: 3,
                    borderRadius: 2,
                    background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    color: 'white'
                }}
            >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Quick Links
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={2}>
                        <Button
                            variant="outlined"
                            fullWidth
                            sx={{
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                            }}
                            onClick={() => {
                                setTabValue(0);
                                setSearchQuery('');
                            }}
                        >
                            Getting Started
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Button
                            variant="outlined"
                            fullWidth
                            sx={{
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                            }}
                            onClick={() => {
                                setSearchQuery('login');
                            }}
                        >
                            Login Help
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Button
                            variant="outlined"
                            fullWidth
                            sx={{
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                            }}
                            onClick={() => {
                                setSearchQuery('classes registration');
                            }}
                        >
                            Class Registration
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Button
                            variant="outlined"
                            fullWidth
                            sx={{
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                            }}
                            onClick={() => {
                                setSearchQuery('payment');
                            }}
                        >
                            Payment Issues
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Button
                            variant="outlined"
                            fullWidth
                            sx={{
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                            }}
                            onClick={() => {
                                setTabValue(6);
                                setSearchQuery('');
                            }}
                        >
                            Contact Support
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Button
                            variant="outlined"
                            fullWidth
                            sx={{
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                            }}
                            onClick={() => {
                                setSearchQuery('contracts');
                            }}
                        >
                            Contracts
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
        </Container>
    );
};

export default Help;